/* ============================================================
   Discover — unified storefront search
   Self-contained: talks to the app proxy at /apps/discover/api
   and renders cards through Shopify's section renderer. Defines:
     <discover-palette>      — header search panel
     <discover-search-page>  — /search experience
     <discover-hero>         — homepage hero search
   <discover-brief> (brief-mode canvas) moved to discover-brief.js on
   2026-09-19 and is not loaded; see that file's header to re-enable.
   No dependency on theme.js internals beyond CSS classes and the
   openable-element event vocabulary (for header toggle links).
   ============================================================ */
(() => {
  'use strict';

  const API_BASE = '/apps/discover/api';
  const FONT_RENDER_BASE = 'https://api.studio2am.co/api/font-render';
  const SEARCH_URL =
    (window.themeVariables && window.themeVariables.routes.searchUrl) || '/search';
  const GRID_SECTION_ID = 'discover-results-grid';
  // Actions on a search already on screen: the backend writes no new row
  // for these, and clicks keep the original query id
  const REFINEMENT_SOURCES = ['facet', 'sort', 'chip_removed', 'pivot'];
  // Returns to results already on record: the back button, a reload, a
  // popstate, leaving image mode. The server writes no row for these and
  // the PostHog search event is skipped; the original query id is reused.
  const HISTORY_SOURCES = ['back', 'reload'];
  const RENDER_CHUNK = 24;

  /* ---------- utilities ---------- */

  function sessionId() {
    try {
      const key = 'dsc_session_id';
      let id = localStorage.getItem(key);
      if (!id) {
        id = `dsc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(key, id);
      }
      return id;
    } catch {
      return 'dsc-ephemeral';
    }
  }

  function debounce(fn, wait) {
    let timer = null;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  async function apiJSON(path, { params, method = 'GET', body, formData } = {}) {
    const qs = params ? `?${new URLSearchParams(params)}` : '';
    const init = { method, headers: { Accept: 'application/json' } };
    if (body) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    } else if (formData) {
      init.body = formData;
    }
    const response = await fetch(API_BASE + path + qs, init);
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload || !payload.success) {
      const error = new Error((payload && payload.error) || `Request failed: ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return payload.data;
  }

  /* Fire-and-forget POST that survives navigation (sendBeacon, then
     keepalive fetch). Never throws, never surfaces a result. */
  function postBeacon(path, payload) {
    const body = JSON.stringify(payload);
    try {
      if (
        navigator.sendBeacon &&
        navigator.sendBeacon(`${API_BASE}${path}`, new Blob([body], { type: 'application/json' }))
      ) {
        return;
      }
    } catch {
      /* fall through */
    }
    fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  }

  /* PostHog mirror of the search log: same ids, so funnels and replays
     join to the Postgres rows. PostHog loads async and is skipped for
     crawlers, so every call is guarded. */
  function track(event, props, options) {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(event, props, options);
      }
    } catch {
      /* analytics never break the page */
    }
  }

  /* The product page reads this to attribute its view to the search that
     led there (product-tracking.js). One entry: the last clicked result. */
  const LAST_CLICK_KEY = 'dsc_last_click';
  function rememberClick(click) {
    try {
      sessionStorage.setItem(LAST_CLICK_KEY, JSON.stringify({ ...click, at: Date.now() }));
    } catch {
      /* storage unavailable */
    }
  }

  /* Query ids of recent searches, keyed by query text, so a return to the
     same results (back button, reload, popstate) attributes clicks to the
     search the server already logged instead of writing a second row. */
  const QUERY_IDS_KEY = 'dsc_query_ids';
  const QUERY_IDS_MAX = 20;
  function queryIdKey(q) {
    return (q || '').trim().toLowerCase();
  }
  function rememberQueryId(q, queryId) {
    if (!q || !queryId) return;
    try {
      const map = JSON.parse(sessionStorage.getItem(QUERY_IDS_KEY) || '{}');
      delete map[queryIdKey(q)];
      map[queryIdKey(q)] = queryId;
      const keys = Object.keys(map);
      while (keys.length > QUERY_IDS_MAX) delete map[keys.shift()];
      sessionStorage.setItem(QUERY_IDS_KEY, JSON.stringify(map));
    } catch {
      /* storage unavailable */
    }
  }
  function recallQueryId(q) {
    try {
      const map = JSON.parse(sessionStorage.getItem(QUERY_IDS_KEY) || '{}');
      return map[queryIdKey(q)] || null;
    } catch {
      return null;
    }
  }
  /* How this page load happened: 'back_forward', 'reload' or 'navigate'. */
  function navigationType() {
    try {
      const entry = performance.getEntriesByType('navigation')[0];
      return entry ? entry.type : 'navigate';
    } catch {
      return 'navigate';
    }
  }

  function openAmi(question, resultCount, queryId) {
    // The search row already carries intent 'question' (server-side), so no
    // second row here. The element reports the hand-off itself as "Ami
    // Opened" with surface discover_band (plan §4.10); the search details
    // ride along on that event.
    const extra = { query_id: queryId || null, query: question, result_count: resultCount };
    if (window.AmiChat) {
      window.AmiChat.open(question, { surface: 'discover_band', track: extra });
    } else if (window.Studio2amHelpWidget) {
      window.Studio2amHelpWidget.open(question);
    } else {
      window.open('https://help.studio2am.co', '_blank', 'noopener');
    }
  }

  /* Recent searches for the panel's zero state: this browser only, newest
     first, written by the search page on every logged search. */
  const RECENT_SEARCHES_KEY = 'dsc_recent_searches';
  const RECENT_SEARCHES_MAX = 6;
  function readRecentSearches() {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
      return Array.isArray(list)
        ? list.filter((term) => typeof term === 'string' && term.trim()).slice(0, RECENT_SEARCHES_MAX)
        : [];
    } catch {
      return [];
    }
  }
  function rememberRecentSearch(q) {
    const term = String(q || '').trim();
    if (!term) return;
    try {
      const key = term.toLowerCase();
      const list = readRecentSearches().filter((existing) => existing.toLowerCase() !== key);
      list.unshift(term);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list.slice(0, RECENT_SEARCHES_MAX)));
    } catch {
      /* storage unavailable */
    }
  }
  /* Focal's product page keeps recently viewed ids under this key (theme.js) */
  const RECENTLY_VIEWED_KEY = 'theme:recently-viewed-products';
  const RECENTLY_VIEWED_MAX = 6;
  function readRecentlyViewedIds() {
    try {
      const items = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      return Array.isArray(items)
        ? items.map((id) => String(id)).filter((id) => /^\d+$/.test(id)).slice(0, RECENTLY_VIEWED_MAX)
        : [];
    } catch {
      return [];
    }
  }

  const ICONS = {
    search:
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>',
    close:
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    chevron:
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>',
    check:
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12.5l5 5L20 6.5"/></svg>',
  };

  /* ---------- font previews (type echo) ---------- */

  const FontPreviews = {
    stylesByShopifyId: new Map(),
    pending: new Map(),

    /** Full styles array ({name, hash}[]) per shopify id, batch-cached. */
    async allStylesFor(shopifyIds) {
      const missing = shopifyIds.filter(
        (id) => !this.stylesByShopifyId.has(id) && !this.pending.has(id)
      );
      if (missing.length > 0) {
        const request = fetch(`${FONT_RENDER_BASE}/styles/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productIds: missing }),
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((payload) => {
            const map = payload && payload.success && payload.data ? payload.data : {};
            missing.forEach((id) => {
              const styles = map[id];
              this.stylesByShopifyId.set(id, Array.isArray(styles) ? styles : []);
              this.pending.delete(id);
            });
          })
          .catch(() => {
            missing.forEach((id) => {
              this.stylesByShopifyId.set(id, []);
              this.pending.delete(id);
            });
          });
        missing.forEach((id) => this.pending.set(id, request));
      }
      await Promise.all(shopifyIds.filter((id) => this.pending.has(id)).map((id) => this.pending.get(id)));
      const result = {};
      shopifyIds.forEach((id) => {
        result[id] = this.stylesByShopifyId.get(id) || [];
      });
      return result;
    },

    /** One product's styles through the GET route, which unlike the batch
        POST carries a Cache-Control the browser can use. */
    async stylesForOne(shopifyId) {
      const known = this.stylesByShopifyId.get(shopifyId);
      if (known) return known;
      const styles = await fetch(`${FONT_RENDER_BASE}/styles/${encodeURIComponent(shopifyId)}`)
        .then((response) => (response.ok ? response.json() : null))
        .then((payload) =>
          payload && payload.success && payload.data && Array.isArray(payload.data.styles)
            ? payload.data.styles
            : []
        )
        .catch(() => []);
      this.stylesByShopifyId.set(shopifyId, styles);
      return styles;
    },

    /** First-style hash per shopify id (null when none) — rail compatibility. */
    async stylesFor(shopifyIds) {
      const all = await this.allStylesFor(shopifyIds);
      const result = {};
      shopifyIds.forEach((id) => {
        const styles = all[id];
        result[id] = styles && styles.length > 0 ? styles[0].hash : null;
      });
      return result;
    },

    /** Vertical metrics of a style in em units (capHeight, frameTop, ...); null on failure. */
    metricsFor(hash) {
      return fetch(`${FONT_RENDER_BASE}/${hash}/metrics`)
        .then((response) => (response.ok ? response.json() : null))
        .then((payload) => (payload && payload.success && payload.data ? payload.data : null))
        .catch(() => null)
        .then((metrics) => {
          if (!metrics || typeof metrics.capHeight !== 'number' || typeof metrics.frameTop !== 'number') {
            throw new Error('font metrics unavailable');
          }
          return metrics;
        });
    },

    /** `height` fits the render to a pixel height; pass null with `extra.size` to set the em instead. */
    src(hash, text, height, bg, fg, extra) {
      const params = new URLSearchParams({
        text: text || 'Aa',
        width: 3000,
        fg: fg || 'FFFFFF',
        bg: bg || '000000',
      });
      if (height) {
        params.set('height', String(height));
        params.set('width', String(Math.min(3000, height * 30)));
      }
      // e.g. { fit: 'ink' } — canvas trimmed to the glyphs, { scale: 2 } for retina
      if (extra) Object.entries(extra).forEach(([key, value]) => params.set(key, String(value)));
      return `${FONT_RENDER_BASE}/${hash}?${params}`;
    },
  };

  /* ---------- card rendering via Shopify section renderer ---------- */

  /**
   * Section-render fetch for card nodes. `complete` is false when any chunk
   * failed in transport — callers that cache "this product has no card" must
   * not do so off a failed fetch.
   */
  async function fetchCardNodes(items) {
    const chunks = [];
    for (let i = 0; i < items.length; i += RENDER_CHUNK) {
      chunks.push(items.slice(i, i + RENDER_CHUNK));
    }
    let complete = true;
    const htmlChunks = await Promise.all(
      chunks.map((chunk) => {
        const query = chunk.map((item) => `id:${item.shopify_id}`).join(' OR ');
        const url = `${SEARCH_URL}?q=${encodeURIComponent(query)}&type=product&section_id=${GRID_SECTION_ID}`;
        return fetch(url)
          .then((response) => {
            if (!response.ok) {
              complete = false;
              return '';
            }
            return response.text();
          })
          .catch(() => {
            complete = false;
            return '';
          });
      })
    );
    const byShopifyId = new Map();
    htmlChunks.forEach((html) => {
      if (!html) return;
      const doc = document.createElement('div');
      doc.innerHTML = html;
      doc.querySelectorAll('.product-item[data-product-id]').forEach((node) => {
        byShopifyId.set(String(node.getAttribute('data-product-id')), node);
      });
    });
    return { byShopifyId, complete };
  }

  async function renderCards(items) {
    if (items.length === 0) return [];
    const { byShopifyId } = await fetchCardNodes(items);
    // Shopify ignores id: order — restore the ranked order here
    return items.map((item) => byShopifyId.get(String(item.shopify_id)) || null).filter(Boolean);
  }

  /* ---------- palette thumbnails, lifted from section-rendered cards ---------- */

  const CardThumbs = {
    cache: new Map(), // shopify_id -> { src, srcset, price } | null (definitively no card)
    pending: new Map(), // shopify_id -> in-flight fetch promise

    async thumbsFor(shopifyIds) {
      const wanted = shopifyIds.map(String);
      const missing = wanted.filter((id) => !this.cache.has(id) && !this.pending.has(id));
      if (missing.length > 0) {
        const request = fetchCardNodes(missing.map((id) => ({ shopify_id: id })))
          .then(({ byShopifyId, complete }) => {
            missing.forEach((id) => {
              const card = byShopifyId.get(id);
              if (card) {
                const img = card.querySelector('img.product-item__primary-image');
                const hover = card.querySelector('img.product-item__secondary-image');
                const link = card.querySelector('a.product-item-meta__title');
                const vendor = card.querySelector('.product-item-meta__link--vendor');
                this.cache.set(id, {
                  vendor: vendor ? vendor.textContent.replace(/\s+/g, ' ').trim() : '',
                  src: img ? img.getAttribute('src') : null,
                  srcset: img ? img.getAttribute('srcset') : null,
                  hoverSrc: hover ? hover.getAttribute('src') : null,
                  hoverSrcset: hover ? hover.getAttribute('srcset') : null,
                  price: CardThumbs.priceText(card),
                  href: link ? link.getAttribute('href') : null,
                  title: link ? link.textContent.replace(/\s+/g, ' ').trim() : '',
                });
              } else if (complete) {
                // The fetch succeeded and Shopify returned no card — the
                // product isn't in the Online Store channel. Cache the miss
                // so keystrokes don't refetch it. A failed fetch leaves the
                // id uncached so the next render retries.
                this.cache.set(id, null);
              }
              this.pending.delete(id);
            });
          })
          .catch(() => {
            missing.forEach((id) => this.pending.delete(id));
          });
        missing.forEach((id) => this.pending.set(id, request));
      }
      await Promise.all(
        wanted.filter((id) => this.pending.has(id)).map((id) => this.pending.get(id))
      );
      const result = {};
      wanted.forEach((id) => {
        result[id] = this.cache.get(id) || null;
      });
      return result;
    },

    priceText(card) {
      const priceEl = card.querySelector('.price-list .price');
      if (!priceEl) return '';
      const clone = priceEl.cloneNode(true);
      clone.querySelectorAll('.visually-hidden').forEach((node) => node.remove());
      return clone.textContent.replace(/\s+/g, ' ').trim();
    },
  };

  /**
   * Cards are injected long after wishlist-button's DOMContentLoaded sync ran
   * — re-fire its update event (the listener reads window.Wishlist directly)
   * so already-saved products render with filled hearts.
   */
  function syncWishlistHearts() {
    // The header's badge listener (sections/header.liquid) reads detail.count,
    // so the detail carries what assets/wishlist.js sends; an empty detail
    // threw there on every card injection.
    const items = window.Wishlist ? window.Wishlist.getItems() : [];
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items, count: items.length } }));
  }

  /** Classification labels are kebab-case slugs ("social-media") — shoppers see words */
  function displayLabel(value) {
    return String(value || '').replace(/-/g, ' ');
  }

  // Facet values arrive lowercase (moods, use cases) — capitalize for display
  function titleLabel(value) {
    const label = displayLabel(value);
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  function highlightMatch(text, term) {
    const index = text.toLowerCase().indexOf(term.toLowerCase());
    if (index === -1) return escapeHtml(text);
    return (
      escapeHtml(text.slice(0, index)) +
      '<mark>' +
      escapeHtml(text.slice(index, index + term.length)) +
      '</mark>' +
      escapeHtml(text.slice(index + term.length))
    );
  }

  /* ============================================================
     <discover-palette> — header search panel
     Opened by Focal's toggle-link machinery (mobile icon sets `.open`)
     or by focusing the header's own search input on lap-and-up widths,
     where the card opens over the tools capsule and that input drives it.
     Dispatches openable-element:open/close so the header trigger aria
     state stays in sync. Same element id as the old drawer.
     ============================================================ */

  const PANEL_WIDE = '(min-width: 1000px)';
  /* Below 1000px the header is one capsule (header.liquid): every card anchors to it */
  const COMPACT_ANCHOR = '.header__wrapper';
  const TILE_TERM_MAX_CHARS = 30;
  let tilesPromise = null; // one /tiles fetch per page

  function fetchTiles() {
    if (!tilesPromise) {
      tilesPromise = apiJSON('/tiles')
        .then((data) =>
          (data && Array.isArray(data.tiles) ? data.tiles : []).filter(
            (tile) =>
              tile &&
              typeof tile.term === 'string' &&
              tile.term.trim() &&
              tile.term.length <= TILE_TERM_MAX_CHARS &&
              /^\d+$/.test(String(tile.shopify_id))
          )
        )
        .catch(() => []); // an old backend or a blip: the row stays hidden
    }
    return tilesPromise;
  }

  /* A creator result goes to the creator's own collection; the creator
     filter on /search is only the fallback for a creator with no synced
     collection yet. */
  function creatorHref(name, collectionHandle) {
    const handle = String(collectionHandle || '').trim();
    if (/^[a-z0-9][a-z0-9-]*$/i.test(handle)) return `/collections/${handle}`;
    return `${SEARCH_URL}?creator=${encodeURIComponent(name || '')}&src=suggest`;
  }

  /** A parsed https URL's serialization, or null: the only form image/banner URLs are used in */
  function safeHttpsUrl(value) {
    try {
      const url = new URL(String(value || ''));
      return url.protocol === 'https:' ? url.href : null;
    } catch {
      return null;
    }
  }

  function avatarHtml(creator, className) {
    const url = safeHttpsUrl(creator && creator.avatar_url);
    const initial = escapeHtml(String((creator && creator.name) || '?').charAt(0).toUpperCase());
    if (url) {
      return `<span class="${className}" data-avatar-initial="${initial}"><img src="${escapeHtml(url)}" alt="" loading="lazy" onerror="this.parentNode.classList.add('${className}--initial');this.parentNode.textContent=this.parentNode.getAttribute('data-avatar-initial')"></span>`;
    }
    return `<span class="${className} ${className}--initial" aria-hidden="true">${initial}</span>`;
  }

  /* ============================================================
     HeaderCard — the base for every card that hangs off the header:
     the search card and the wishlist card today, the cart later. Owns
     open/close (with the openable-element events Focal's aria wiring
     listens for), the scroll lock, the geometry against an anchor in
     the header, Escape and outside-click closing, breakpoint and resize
     handling, and the rule that only one header card is open at a time.
     A subclass names its anchor (`anchorSelector`), its triggers outside
     the card (`triggers()`), and fills in wire()/onOpen()/onClose().
     ============================================================ */

  const openCards = new Set();

  class HeaderCard extends HTMLElement {
    /** The header element the card hangs under and matches in width */
    get anchorSelector() {
      return '.header__wrapper';
    }

    connectedCallback() {
      // Theme-editor re-renders produce a second element with this id — the
      // fresh render replaces the old one (mirrors Focal's OpenableElement)
      const existing = document.getElementById(this.id);
      if (existing && existing !== this) {
        existing.replaceWith(this);
        return; // connectedCallback re-runs after the move
      }
      if (this._wired) return;
      this._wired = true;

      this.wideQuery = window.matchMedia(PANEL_WIDE);
      this.addEventListener('click', (event) => this.onClick(event));

      this._onDocKeydown = (event) => {
        if (event.key === 'Escape' && this.open) {
          this.open = false;
          this.onEscape();
        }
      };
      document.addEventListener('keydown', this._onDocKeydown);

      // Desktop: a click anywhere outside the card and its triggers closes it
      this._onDocPointerDown = (event) => {
        if (!this.open || !this.wideQuery.matches) return;
        if (this.contains(event.target)) return;
        if (this.ownsTarget(event.target)) return;
        this.open = false;
      };
      document.addEventListener('pointerdown', this._onDocPointerDown);

      // Crossing the breakpoint changes what drives the card: close and restart
      this._onBreakpoint = () => {
        if (this.open) this.open = false;
      };
      this.wideQuery.addEventListener('change', this._onBreakpoint);
      this._onResize = () => {
        if (this.open) this.position();
      };
      window.addEventListener('resize', this._onResize);

      this.wire();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onDocKeydown);
      document.removeEventListener('pointerdown', this._onDocPointerDown);
      window.removeEventListener('resize', this._onResize);
      if (this.wideQuery) this.wideQuery.removeEventListener('change', this._onBreakpoint);
      openCards.delete(this);
      // Removed while open (editor re-render) — never leave the page locked
      if (this.open) document.documentElement.classList.remove('lock-all', 'dsc-card-open');
    }

    /* Subclass hooks */
    wire() {}
    onOpen() {}
    onClose() {}
    onEscape() {}
    /** The card's head icon and the capsule icon it takes over from */
    headIcon() {
      return null;
    }
    triggerIcon() {
      return null;
    }
    /** Elements outside the card (its triggers) a click on must not close it */
    triggers() {
      return [];
    }
    ownsTarget(target) {
      return this.triggers().some((el) => el && el.contains(target));
    }
    /** True when the click was the close control or the overlay */
    onClick(event) {
      if (
        event.target.closest('[data-action="close"]') ||
        event.target.classList.contains('dsc-panel__overlay')
      ) {
        this.open = false;
        return true;
      }
      return false;
    }

    get open() {
      return this.hasAttribute('open');
    }

    set open(value) {
      if (value === this.open) return;
      if (value) {
        // One card at a time: opening the wishlist closes the search card,
        // and either closes Focal's cart drawer (a header card on desktop)
        openCards.forEach((card) => {
          if (card !== this) card.open = false;
        });
        closeCartCard();
        closeMenuCard();
        // Lock first: removing the page scrollbar can shift the header's
        // container, and the card is measured against it
        document.documentElement.classList.add('lock-all');
        this.position();
        stageCardIcon(this, this.headIcon(), this.triggerIcon());
        this.clientWidth; // flush layout so the CSS transition runs
        this.setAttribute('open', '');
        document.documentElement.classList.add('dsc-card-open'); // fades the capsule's contents
        openCards.add(this);
        this.dispatchEvent(new CustomEvent('openable-element:open', { bubbles: true }));
        this.setTriggerState(true);
        this.onOpen();
      } else {
        this.removeAttribute('open');
        openCards.delete(this);
        document.documentElement.classList.remove('lock-all');
        document.documentElement.classList.remove('dsc-card-open');
        this.dispatchEvent(new CustomEvent('openable-element:close', { bubbles: true }));
        this.setTriggerState(false);
        this.onClose();
      }
    }

    setTriggerState(isOpen) {
      this.triggers().forEach((el) => {
        if (el) el.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }

    /* Desktop: the card opens over its anchor in the header's capsule row,
       from the capsule's own outline (same top, left and width, so the
       capsule reads as expanding into the card). Search, wishlist and cart
       all anchor to the tools capsule. The veil starts at the row's bottom
       edge so the rest of the header stays live. The page is scroll-locked
       while open, so one measurement holds. */
    position() {
      // Compact: the same morph from the one capsule, whatever the subclass names on desktop
      applyHeaderGeometry(this, this.wideQuery.matches ? this.anchorSelector : COMPACT_ANCHOR);
    }
  }

  /* The trigger's icon carries the eye across the morph: the card's head
     icon starts where that icon sits in the capsule and slides home
     (discover.css reads --dsc-icon-dx/dy). Measured with the card already
     placed and the offset zeroed, so the two rects compare like for like. */
  function stageCardIcon(el, head, from) {
    el.style.setProperty('--dsc-icon-dx', '0px');
    el.style.setProperty('--dsc-icon-dy', '0px');
    if (!head || !from) return;
    const a = from.getBoundingClientRect();
    const b = head.getBoundingClientRect();
    if (!a.width || !b.width) return;
    el.style.setProperty('--dsc-icon-dx', `${Math.round(a.left + a.width / 2 - (b.left + b.width / 2))}px`);
    el.style.setProperty('--dsc-icon-dy', `${Math.round(a.top + a.height / 2 - (b.top + b.height / 2))}px`);
  }

  /** The first laid-out (visible) element among candidates */
  function visibleOne(elements) {
    return elements.find((el) => el && el.getBoundingClientRect().width > 0) || null;
  }

  /** Lay `el` over the header element `anchorSelector` matches: its top, left and width */
  function applyHeaderGeometry(el, anchorSelector) {
    const header = document.querySelector('.header');
    const row = header && (header.querySelector('.header__wrapper') || header);
    const anchor = (header && header.querySelector(anchorSelector)) || row;
    const rowRect = row ? row.getBoundingClientRect() : null;
    const rect = anchor ? anchor.getBoundingClientRect() : rowRect;
    const rowBottom = rowRect ? Math.max(0, Math.round(rowRect.bottom)) : 0;
    const top = rect ? Math.max(0, Math.round(rect.top)) : 0;
    const left = rect ? Math.max(12, Math.round(rect.left)) : 24;
    const width = rect ? Math.round(rect.width) : 760;
    el.style.setProperty('--dsc-panel-top', `${top}px`);
    el.style.setProperty('--dsc-panel-left', `${left}px`);
    el.style.setProperty('--dsc-panel-width', `${width}px`);
    el.style.setProperty('--dsc-veil-top', `${rowBottom}px`);
  }

  class DiscoverPalette extends HeaderCard {
    /** Opens over the tools capsule, where the search field lives */
    get anchorSelector() {
      return '.header__capsule--tools';
    }

    wire() {
      this.sheetInput = this.querySelector('[data-panel-input]');
      this.resultsEl = this.querySelector('[data-palette-results]');
      this.zeroEl = this.querySelector('[data-palette-zero]');
      this.loadingEl = this.querySelector('[data-palette-loading]');
      this.tilesEl = this.querySelector('[data-panel-tiles]');
      this.tilesGridEl = this.querySelector('[data-panel-tiles-grid]');
      this.recentEl = this.querySelector('[data-panel-recent]');
      this.recentSearchesEl = this.querySelector('[data-panel-recent-searches]');
      this.recentProductsEl = this.querySelector('[data-panel-recent-products]');
      this._cursorIndex = -1;
      this._tilesRendered = false;

      this.bindInput(this.sheetInput);
      this.bindHeaderInput();
    }

    triggers() {
      return this.headerInput ? [this.headerInput] : [];
    }

    headIcon() {
      return this.querySelector('.dsc-panel__head svg');
    }

    /** The capsule field's icon, or the compact capsule's search icon while the field is hidden */
    triggerIcon() {
      const form = this.headerInput && this.headerInput.closest('form');
      return visibleOne([
        form ? form.querySelector('svg') : null,
        ...Array.from(document.querySelectorAll(`[aria-controls="${this.id}"] svg`)),
      ]);
    }

    /** A click inside the header pill's form must not close the card */
    ownsTarget(target) {
      const headerForm = this.headerInput && this.headerInput.closest('form');
      return !!headerForm && headerForm.contains(target);
    }

    onEscape() {
      if (this.headerInput) this.headerInput.blur();
    }

    /* The header's search pill (header.liquid) is the trigger on lap-and-up:
       focusing it opens the card under the header and hands the value and
       the caret to the card's own input. While the card is open the pill
       mirrors what is typed, and on close the text is handed back, so the
       pill always reads as the search box. */
    bindHeaderInput() {
      this.headerInput = document.querySelector('[data-dsc-panel-input]');
      if (!this.headerInput) return;
      if (this.sheetInput) {
        this.sheetInput.addEventListener('input', () => {
          if (this.open && this.wideQuery.matches) this.headerInput.value = this.sheetInput.value;
        });
      }
      const openFromHeader = () => {
        if (!this.wideQuery.matches) return;
        if (!this.open) this.open = true;
        this.takeOver();
      };
      this.headerInput.addEventListener('focus', openFromHeader);
      this.headerInput.addEventListener('click', openFromHeader);
      // A keystroke that lands before the hand-off is mirrored, not lost
      this.headerInput.addEventListener('input', () => {
        if (this.open && this.wideQuery.matches) this.takeOver();
      });
    }

    takeOver() {
      if (!this.sheetInput || !this.headerInput) return;
      if (this.sheetInput.value !== this.headerInput.value) {
        this.sheetInput.value = this.headerInput.value;
        this.query(this.sheetInput.value);
      }
      if (document.activeElement !== this.sheetInput) {
        this.sheetInput.focus();
        const end = this.sheetInput.value.length;
        try {
          this.sheetInput.setSelectionRange(end, end);
        } catch {
          /* unsupported input state */
        }
      }
    }

    bindInput(input) {
      if (!input) return;
      const form = input.closest('form');
      if (form && !form._dscBound) {
        form._dscBound = true;
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.go(input.value, 'header');
        });
      }
      input.addEventListener(
        'input',
        debounce(() => {
          if (input !== this.activeInput) return;
          this.query(input.value);
        }, 180)
      );
      input.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    /** The card's own input drives the panel at every width */
    get activeInput() {
      return this.sheetInput;
    }

    onOpen() {
      const input = this.activeInput;
      if (this.wideQuery.matches && this.headerInput) {
        this.takeOver();
      } else {
        setTimeout(() => input && input.focus(), 150);
      }
      const term = input ? input.value.trim() : '';
      track('Discover Panel Opened', {
        state: term.length >= 2 ? 'typed' : 'zero',
        path: window.location.pathname,
      });
      if (term.length >= 2) this.query(term);
      else this.showZero(true);
    }

    onClose() {
      // Hand the text back so the bar shows what was typed
      if (this.headerInput && this.sheetInput && this.wideQuery.matches) {
        this.headerInput.value = this.sheetInput.value;
      }
      if (this.sheetInput && document.activeElement === this.sheetInput) this.sheetInput.blur();
    }

    onClick(event) {
      if (super.onClick(event)) return;
      const lane = event.target.closest('[data-lane]');
      if (lane) this.trackClick(lane);
      const goChip = event.target.closest('[data-palette-q]');
      if (goChip) {
        event.preventDefault();
        this.go(goChip.getAttribute('data-palette-q'), goChip.getAttribute('data-src') || 'suggest');
        return;
      }
    }

    trackClick(el) {
      const input = this.activeInput;
      track('Discover Panel Clicked', {
        lane: el.getAttribute('data-lane'),
        position: Number(el.getAttribute('data-position')) || 0,
        term: el.getAttribute('data-term') || null,
        product_id: el.getAttribute('data-product-id') || null,
        query_length: input ? input.value.trim().length : 0,
      });
    }

    go(q, source) {
      const query = String(q || '').trim();
      if (query.length === 0) return;
      window.location.assign(
        `${SEARCH_URL}?q=${encodeURIComponent(query)}&src=${encodeURIComponent(source || 'header')}`
      );
    }

    async query(raw) {
      const term = String(raw || '').trim();
      this._cursorIndex = -1;
      // Always retire the previous in-flight term — otherwise backspacing
      // below 2 chars lets a stale response overwrite the zero state
      this._activeTerm = term;
      if (term.length < 2) {
        this.showZero(true);
        return;
      }
      this.zeroEl.hidden = true;
      this.loadingEl.hidden = false;
      try {
        const data = await apiJSON('/suggest', { params: { q: term } });
        if (this._activeTerm !== term) return; // stale response
        this.loadingEl.hidden = true;
        this.renderResults(term, data);
      } catch {
        if (this._activeTerm !== term) return;
        this.showZero(true);
      }
    }

    showZero(show) {
      this.loadingEl.hidden = true;
      this.zeroEl.hidden = !show;
      this.resultsEl.hidden = show;
      if (show) {
        this.resultsEl.innerHTML = '';
        this.renderRecent();
        this.renderTiles();
      }
    }

    /* ---------- zero state ---------- */

    async renderTiles() {
      if (this._tilesRendered || !this.tilesEl) return;
      const tiles = await fetchTiles();
      if (this._tilesRendered) return;
      this._tilesRendered = true;
      if (tiles.length === 0) {
        // Nothing published (or an old backend): drop the skeletons and the row
        this.tilesGridEl.innerHTML = '';
        this.tilesEl.hidden = true;
        return;
      }
      // Replaces the Liquid skeleton tiles; each image slot keeps shimmering until its cover loads
      this.tilesGridEl.innerHTML = tiles
        .map((tile, index) => {
          const term = escapeHtml(tile.term);
          return `<a class="dsc-tile" data-lane="tile" data-position="${index}" data-term="${term}" href="${SEARCH_URL}?q=${encodeURIComponent(tile.term)}&src=tile"><span class="dsc-tile__img" data-tile-id="${escapeHtml(String(tile.shopify_id))}"></span><span class="dsc-tile__label">${term}</span></a>`;
        })
        .join('');
      this.tilesEl.hidden = false;
      this.tilesGridEl.removeAttribute('aria-busy');
      const thumbs = await CardThumbs.thumbsFor(tiles.map((tile) => String(tile.shopify_id)));
      this.tilesGridEl.querySelectorAll('[data-tile-id]').forEach((slot) => {
        const info = thumbs[slot.getAttribute('data-tile-id')];
        if (!info || !info.src) {
          slot.classList.add('is-empty'); // no cover: plain tile, no shimmer
          return;
        }
        const img = document.createElement('img');
        img.alt = '';
        img.loading = 'lazy';
        if (info.srcset) {
          img.srcset = info.srcset;
          img.sizes = '(min-width: 1000px) 140px, 25vw';
        }
        img.src = info.src;
        img.onload = () => slot.classList.add('is-loaded');
        img.onerror = () => slot.classList.add('is-empty');
        slot.appendChild(img);
      });
    }

    renderRecent() {
      if (!this.recentEl) return;
      const searches = readRecentSearches();
      const viewed = readRecentlyViewedIds();
      this.recentSearchesEl.innerHTML = searches
        .map(
          (term, index) =>
            `<button type="button" class="dsc-chip" data-lane="recent_search" data-position="${index}" data-term="${escapeHtml(term)}" data-palette-q="${escapeHtml(term)}" data-src="recent">${ICONS.search}${escapeHtml(term)}</button>`
        )
        .join('');
      this.recentEl.hidden = searches.length === 0 && viewed.length === 0;
      this.renderRecentProducts(viewed);
    }

    async renderRecentProducts(ids) {
      const key = ids.join(',');
      if (this._recentKey === key) return; // same ids as last open: keep the row
      this._recentKey = key;
      this.recentProductsEl.hidden = true;
      this.recentProductsEl.innerHTML = '';
      if (ids.length === 0) return;
      const thumbs = await CardThumbs.thumbsFor(ids);
      if (this._recentKey !== key) return;
      const html = ids
        .map((id, index) => {
          const info = thumbs[id];
          if (!info || !info.src || !info.href) return '';
          const title = escapeHtml(info.title || '');
          const srcset = info.srcset ? ` srcset="${escapeHtml(info.srcset)}" sizes="(min-width: 1000px) 100px, 16vw"` : '';
          return `<a class="dsc-recent-product" data-lane="recent_product" data-position="${index}" data-product-id="${escapeHtml(id)}" href="${escapeHtml(info.href)}" title="${title}"><img src="${escapeHtml(info.src)}"${srcset} alt="${title}" loading="lazy"></a>`;
        })
        .join('');
      if (!html) return;
      this.recentProductsEl.innerHTML = html;
      this.recentProductsEl.hidden = false;
      this.recentEl.hidden = false;
    }

    /* ---------- typed state ---------- */

    renderResults(term, data) {
      const products = Array.isArray(data.products) ? data.products : [];
      const creators = Array.isArray(data.creators) ? data.creators : [];
      const completions = Array.isArray(data.completions) ? data.completions : [];
      const featured = data.featured || null;
      const hasAny =
        products.length > 0 || creators.length > 0 || completions.length > 0 || !!featured;

      this.zeroEl.hidden = true;
      this.resultsEl.hidden = false;

      if (!hasAny) {
        this.resultsEl.innerHTML = `<p class="dsc-panel__empty">Nothing yet for “${escapeHtml(term)}”. Press Enter to search everything.</p>`;
        return;
      }

      let html = '';

      if (completions.length > 0) {
        html += '<div class="dsc-lane"><div>';
        completions.forEach((completion, index) => {
          html += `<button type="button" class="dsc-completion" data-nav data-lane="completion" data-position="${index}" data-term="${escapeHtml(completion)}" data-palette-q="${escapeHtml(completion)}">${ICONS.search}<span>${escapeHtml(completion)}</span></button>`;
        });
        html += '</div></div>';
      }

      if (featured) html += this.featuredHtml(featured, term);

      if (products.length > 0) {
        html += '<div class="dsc-lane"><span class="dsc-kicker dsc-lane__title">Products</span><div class="dsc-panel__hits">';
        products.forEach((product, index) => {
          html += this.hitHtml(product, term, index);
        });
        html += '</div></div>';
      }

      const featuredCreatorName =
        featured && featured.kind === 'creator' && featured.creator ? featured.creator.name : null;
      const creatorRows = creators.filter((creator) => creator.name !== featuredCreatorName);
      if (creatorRows.length > 0) {
        html += '<div class="dsc-lane"><span class="dsc-kicker dsc-lane__title">Creators</span><div>';
        creatorRows.forEach((creator, index) => {
          html += `<a class="dsc-creator" data-nav data-lane="creator" data-position="${index}" data-term="${escapeHtml(creator.name)}" href="${escapeHtml(creatorHref(creator.name, creator.collection_handle))}">${avatarHtml(creator, 'dsc-creator__avatar')}<span class="dsc-creator__name">${escapeHtml(creator.name)}</span><span class="dsc-creator__count">${Number(creator.product_count) || 0} products</span></a>`;
        });
        html += '</div></div>';
      }

      html += `<button type="button" class="dsc-panel__all" data-lane="see_all" data-position="0" data-term="${escapeHtml(term)}" data-palette-q="${escapeHtml(term)}" data-src="header">See all results →</button>`;

      this.resultsEl.innerHTML = html;
      const thumbIds = products.map((product) => String(product.shopify_id));
      if (featured && featured.kind === 'product' && featured.product) {
        thumbIds.push(String(featured.product.shopify_id)); // the tile's price lands with the thumbs
      }
      this.loadThumbs(term, thumbIds);
      if (featured) this.loadFeatureMedia(term, featured);
    }

    hitHtml(product, term, index, lane) {
      const meta =
        escapeHtml(product.creator_name || '') +
        (product.category ? ` · ${escapeHtml(product.category)}` : '');
      return `<a class="dsc-hit" data-nav data-lane="${lane || 'product'}" data-position="${index}" data-product-id="${escapeHtml(String(product.shopify_id))}" href="/products/${escapeHtml(product.handle)}"><span class="dsc-hit__thumb" data-thumb-id="${escapeHtml(String(product.shopify_id))}"></span><span class="dsc-hit__body"><span class="dsc-hit__title">${escapeHtml(product.title)}</span><span class="dsc-hit__meta">${meta}</span></span><span class="dsc-hit__price${product.is_free ? ' dsc-hit__price--free' : ''}" data-thumb-price="${escapeHtml(String(product.shopify_id))}">${product.is_free ? 'Free' : ''}</span></a>`;
    }

    /* One tile above the hits: the backend only sends `featured` when the
       top hit is unmistakably what was typed, or when it is the only hit
       (suggest-featured.ts). Both tiles are deliberately spare: one square,
       a title, one line of facts. The whole tile is clickable, so it carries
       no CTA links, and it repeats nothing the hit rows already say. */
    featuredHtml(featured, term) {
      if (featured.kind === 'product' && featured.product) return this.featuredProductHtml(featured);
      if (featured.kind === 'creator' && featured.creator) return this.featuredCreatorHtml(featured, term);
      return '';
    }

    featuredProductHtml(featured) {
      const product = featured.product;
      const creator = featured.creator || null;
      const id = escapeHtml(String(product.shopify_id));
      const href = `/products/${escapeHtml(product.handle)}`;
      const creatorName = escapeHtml(product.creator_name || '');
      const creatorLink = creatorHref(product.creator_name, creator && creator.collection_handle);
      const creatorRow = creatorName
        ? `<a class="dsc-feature__by" data-lane="featured_creator" data-position="0" data-term="${creatorName}" href="${escapeHtml(creatorLink)}">${avatarHtml(creator || { name: product.creator_name }, 'dsc-feature__by-avatar')}<span>${creatorName}</span></a>`
        : '';
      const price = product.is_free
        ? '<span class="dsc-feature__price dsc-hit__price--free">Free</span>'
        : `<span class="dsc-feature__price" data-thumb-price="${id}"></span>`;
      return `<div class="dsc-lane"><div class="dsc-feature dsc-feature--product"><a class="dsc-feature__gallery" data-nav data-lane="featured_product" data-position="0" data-product-id="${id}" href="${href}" aria-label="${escapeHtml(product.title)}"><span class="dsc-feature__img" data-feature-id="${id}"></span><span class="dsc-feature__img dsc-feature__img--hover" data-feature-hover="${id}" hidden></span></a><div class="dsc-feature__body"><span class="dsc-feature__kicker">Top match</span><div class="dsc-feature__head"><a class="dsc-feature__title" data-lane="featured_product" data-position="0" data-product-id="${id}" href="${href}">${escapeHtml(product.title)}</a>${price}</div><div class="dsc-feature__facts">${creatorRow}</div></div></div></div>`;
    }

    featuredCreatorHtml(featured, term) {
      const creator = featured.creator;
      const name = escapeHtml(creator.name);
      const count = Number(creator.product_count) || 0;
      const href = escapeHtml(creatorHref(creator.name, creator.collection_handle));
      const facts = [`${count} products`].concat(creator.country ? [escapeHtml(String(creator.country))] : []);
      // Whole covers only: the strip is a glimpse of the catalogue, never a
      // cropped fourth. The backend sends three; the slice guards old cache.
      const products = (Array.isArray(featured.products) ? featured.products : []).slice(0, 3);
      const covers = products
        .map(
          (product, index) =>
            `<a class="dsc-feature__cover" data-lane="featured_creator_product" data-position="${index}" data-product-id="${escapeHtml(String(product.shopify_id))}" href="/products/${escapeHtml(product.handle)}" title="${escapeHtml(product.title)}"><span class="dsc-feature__cover-img" data-feature-id="${escapeHtml(String(product.shopify_id))}"></span></a>`
        )
        .join('');
      return `<div class="dsc-lane"><div class="dsc-feature dsc-feature--creator"><a class="dsc-feature__portrait" data-nav data-lane="featured_creator" data-position="0" data-term="${name}" href="${href}" aria-label="${name}">${avatarHtml(creator, 'dsc-feature__avatar')}</a><div class="dsc-feature__body"><span class="dsc-feature__kicker">Creator</span><a class="dsc-feature__title" data-lane="featured_creator" data-position="0" data-term="${name}" href="${href}">${name}</a><div class="dsc-feature__facts">${facts.map((fact) => `<span class="dsc-feature__fact">${fact}</span>`).join('')}</div></div>${
        covers ? `<div class="dsc-feature__covers">${covers}</div>` : ''
      }</div></div>`;
    }

    /* Big images for the feature tiles: the card's primary image at tile
       size, with the card's hover image stacked on top and faded in on
       hover, the way the masonry grid swaps its secondary image. */
    async loadFeatureMedia(term, featured) {
      const ids = [];
      if (featured.kind === 'product' && featured.product) ids.push(String(featured.product.shopify_id));
      if (featured.kind === 'creator' && Array.isArray(featured.products)) {
        featured.products.forEach((product) => ids.push(String(product.shopify_id)));
      }
      if (ids.length === 0) return;
      const thumbs = await CardThumbs.thumbsFor(ids);
      if (this._activeTerm !== term) return;
      this.resultsEl.querySelectorAll('[data-feature-id]').forEach((slot) => {
        const info = thumbs[slot.getAttribute('data-feature-id')];
        if (!info || !info.src) return;
        const big = slot.classList.contains('dsc-feature__img');
        const img = document.createElement('img');
        img.alt = '';
        img.src = info.src;
        if (info.srcset) {
          img.srcset = info.srcset;
          img.sizes = big ? '120px' : '64px';
        }
        img.onload = () => slot.classList.add('is-loaded');
        slot.appendChild(img);
      });
      // The card's hover image, stacked over the cover; the slot stays
      // hidden when the card has no second image
      this.resultsEl.querySelectorAll('[data-feature-hover]').forEach((slot) => {
        const info = thumbs[slot.getAttribute('data-feature-hover')];
        if (!info || !info.hoverSrc) return;
        const img = document.createElement('img');
        img.alt = '';
        img.src = info.hoverSrc;
        if (info.hoverSrcset) {
          img.srcset = info.hoverSrcset;
          img.sizes = '120px';
        }
        img.onload = () => slot.classList.add('is-loaded');
        slot.appendChild(img);
        slot.hidden = false;
      });
    }

    /* Thumbnails + prices arrive from one section-render fetch; rows render
       immediately with placeholder squares and the images fade in */
    async loadThumbs(term, ids) {
      if (ids.length === 0) return;
      const thumbs = await CardThumbs.thumbsFor(ids);
      if (this._activeTerm !== term) return;
      this.resultsEl.querySelectorAll('[data-thumb-id]').forEach((slot) => {
        const info = thumbs[slot.getAttribute('data-thumb-id')];
        if (!info || !info.src) return;
        const img = document.createElement('img');
        img.alt = '';
        if (info.srcset) {
          img.srcset = info.srcset;
          img.sizes = '48px';
        }
        img.src = info.src;
        img.onload = () => slot.classList.add('is-loaded');
        slot.innerHTML = '';
        slot.appendChild(img);
      });
      this.resultsEl.querySelectorAll('[data-thumb-price]').forEach((priceEl) => {
        if (priceEl.textContent) return; // free products keep their badge
        const info = thumbs[priceEl.getAttribute('data-thumb-price')];
        if (info && info.price) priceEl.textContent = info.price;
      });
    }

    onKeydown(event) {
      if (!this.open) return;
      const navItems = Array.from(this.resultsEl.querySelectorAll('[data-nav]'));
      if (navItems.length === 0) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this._cursorIndex = Math.min(Math.max(this._cursorIndex + delta, 0), navItems.length - 1);
        navItems.forEach((item, index) =>
          item.classList.toggle('is-cursor', index === this._cursorIndex)
        );
        navItems[this._cursorIndex].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this._cursorIndex >= 0) {
        event.preventDefault();
        navItems[this._cursorIndex].click();
      }
    }
  }

  window.customElements.define('discover-palette', DiscoverPalette);

  /* ============================================================
     <discover-wishlist> — the header heart's dropdown
     Lap and up: the heart toggles a card that opens over the tools
     capsule at that capsule's width. Pocket: the heart stays a plain link
     to /pages/wishlist. Rows come from window.Wishlist (wishlist.js: ids
     and handles in localStorage, synced for signed-in customers) and the
     same section-rendered thumbnails as the search card, so nothing here
     needs the backend. Scope: docs/20260918-wishlist-dropdown-scope.md.
     ============================================================ */

  const WISHLIST_ROWS = 8;
  const WISHLIST_URL = '/pages/wishlist';

  class DiscoverWishlist extends HeaderCard {
    get anchorSelector() {
      return '.header__capsule--tools';
    }

    triggers() {
      return this._triggers || [];
    }

    headIcon() {
      return this.querySelector('.dsc-panel__title svg');
    }

    triggerIcon() {
      return visibleOne(this.triggers().map((el) => el.querySelector('svg')));
    }

    wire() {
      this.listEl = this.querySelector('[data-wishlist-list]');
      this.loadingEl = this.querySelector('[data-wishlist-loading]');
      this.emptyEl = this.querySelector('[data-wishlist-empty]');
      this.footEl = this.querySelector('[data-wishlist-foot]');
      this.allEl = this.querySelector('[data-wishlist-all]');
      this.countEl = this.querySelector('[data-wishlist-panel-count]');
      this.icons = this.rowIcons();
      this._triggers = Array.from(document.querySelectorAll('[data-wishlist-header-link]'));
      this._triggers.forEach((el) => {
        el.addEventListener('click', (event) => {
          // Every width since the compact capsule (Sep 2026); the href stays for no-JS
          event.preventDefault();
          this.open = !this.open;
        });
      });
      // Hearts toggled anywhere (a row's Remove, a card behind the veil,
      // the backend sync landing) redraw the open card
      this._onUpdate = () => {
        if (this.open) this.render();
      };
      document.addEventListener('wishlist:updated', this._onUpdate);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener('wishlist:updated', this._onUpdate);
    }

    onOpen() {
      track('Wishlist Panel Opened', { count: this.items().length, path: window.location.pathname });
      this.render();
    }

    onClick(event) {
      if (super.onClick(event)) return;
      // The search card's Panel Clicked, for the wishlist's lanes (row, add, remove, view all)
      const laneEl = event.target.closest('[data-lane]');
      if (laneEl) {
        track('Wishlist Panel Clicked', {
          lane: laneEl.getAttribute('data-lane'),
          position: Number(laneEl.getAttribute('data-position') || 0),
          product_id: laneEl.getAttribute('data-product-id') || null,
          count: this.items().length,
          path: window.location.pathname,
        });
      }
      if (event.target.closest('[data-quick-buy]')) return; // the quick-buy card's own listener
      const remove = event.target.closest('[data-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        this.trackClick(remove);
        if (window.Wishlist) window.Wishlist.remove(Number(remove.getAttribute('data-product-id')));
        return;
      }
      const lane = event.target.closest('[data-lane]');
      if (lane) this.trackClick(lane);
    }

    trackClick(el) {
      track('Discover Panel Clicked', {
        lane: el.getAttribute('data-lane'),
        position: Number(el.getAttribute('data-position')) || 0,
        term: null,
        product_id: el.getAttribute('data-product-id') || null,
        query_length: 0,
      });
    }

    /** Newest first: wishlist.js appends, so the stored order is oldest first */
    items() {
      if (!window.Wishlist) return [];
      return window.Wishlist.getItems().slice().reverse();
    }

    async render() {
      const items = this.items();
      const key = items.map((item) => item.id).join(',');
      this._renderKey = key;
      if (this.countEl) {
        this.countEl.textContent = items.length === 1 ? '1 item' : `${items.length} items`;
      }
      if (items.length === 0) {
        this.loadingEl.hidden = true;
        this.listEl.hidden = true;
        this.listEl.innerHTML = '';
        this.footEl.hidden = true;
        this.emptyEl.hidden = false;
        return;
      }
      this.emptyEl.hidden = true;
      // The spinner only for the first paint: a redraw keeps the old rows
      // on screen until the new ones are ready
      if (this.listEl.children.length === 0) this.loadingEl.hidden = false;
      const shown = items.slice(0, WISHLIST_ROWS);
      const thumbs = await CardThumbs.thumbsFor(shown.map((item) => String(item.id)));
      if (this._renderKey !== key) return; // the list changed while fetching
      this.loadingEl.hidden = true;
      this.listEl.innerHTML = shown
        .map((item, index) => this.rowHtml(item, thumbs[String(item.id)], index))
        .join('');
      this.listEl.hidden = false;
      if (this.allEl) this.allEl.textContent = `View all ${items.length} →`;
      this.footEl.hidden = false;
    }

    /* The row actions' icons from the panel's template (wishlist-panel.liquid
       renders them through the theme's icon snippet); ICONS.close if the
       template is missing */
    rowIcons() {
      const template = this.querySelector('[data-wishlist-icons]');
      const pick = (name) => {
        const el = template && template.content.querySelector(`[data-icon="${name}"]`);
        return el ? el.innerHTML.trim() : '';
      };
      return { add: pick('add'), remove: pick('remove') || ICONS.close };
    }

    /* The mini-cart's line item (sections/mini-cart.liquid: 92px image,
       vendor over the title, price under it where the cart prints the
       licence, centred against the image) plus
       the two icon squares: Add (the quick-buy card) and Remove. The vendor
       is a span, not the cart's collection link. A product with no card (no
       longer in the Online Store channel) is skipped, not deleted, like the
       page does. */
    rowHtml(item, info, index) {
      if (!info || !info.src) return '';
      const id = escapeHtml(String(item.id));
      const href = escapeHtml(info.href || (item.handle ? `/products/${item.handle}` : WISHLIST_URL));
      const title = escapeHtml(info.title || item.handle || '');
      const lane = `data-lane="wishlist_product" data-position="${index}" data-product-id="${id}"`;
      const srcset = info.srcset ? ` srcset="${escapeHtml(info.srcset)}" sizes="92px"` : '';
      const vendor = info.vendor
        ? `<span class="product-item-meta__vendor heading heading--xxsmall">${escapeHtml(info.vendor)}</span>`
        : '';
      // Under the title, where the cart's line item prints the licence
      // (its phone price-list container: the same classes)
      const price = info.price
        ? `<div class="product-item-meta__price-list-container text--small"><div class="price-list"><span class="price${/free/i.test(info.price) ? ' price--highlight' : ''}">${escapeHtml(info.price)}</span></div></div>`
        : '';
      return `<div class="line-item line-item--centered dsc-wishlist__row" data-product-id="${id}"><div class="line-item__content-wrapper"><a class="line-item__image-wrapper" ${lane} href="${href}" tabindex="-1" aria-hidden="true"><img class="line-item__image" src="${escapeHtml(info.src)}"${srcset} alt="" loading="lazy"></a><div class="line-item__info"><div class="product-item-meta">${vendor}<a class="product-item-meta__title text--small" ${lane} href="${href}">${title}</a>${price}</div></div><span class="dsc-hit__actions dsc-wishlist__actions"><button type="button" class="dsc-icon-btn dsc-icon-btn--add tap-area" data-quick-buy="${href}" data-quick-buy-from="wishlist" data-lane="wishlist_add" data-position="${index}" data-product-id="${id}" aria-label="Add ${title} to cart" title="Add to cart">${this.icons.add}</button><button type="button" class="dsc-icon-btn dsc-icon-btn--remove tap-area" data-wishlist-remove data-lane="wishlist_remove" data-position="${index}" data-product-id="${id}" aria-label="Remove ${title} from wishlist" title="Remove">${this.icons.remove}</button></span></div></div>`;
    }
  }

  window.customElements.define('discover-wishlist', DiscoverWishlist);

  /* ============================================================
     <discover-quick-buy> — Focal's quick-buy as a header card
     (snippets/quick-buy-panel.liquid). Any [data-quick-buy="<product
     url>"] control opens it over the tools capsule (the cart card's
     People-also-buy rows, the wishlist card's rows), loads the product's
     own quick-buy view (?view=quick-buy-drawer: the licence selector and
     Add to cart, custom elements from theme.js and custom.js upgrade on
     insertion) and closes on variant:added; Focal's cart:refresh then
     opens the cart card on top, since the form is not inside a .drawer.
     Back reopens the card it came from.
     ============================================================ */

  const QUICK_BUY_VIEW = 'quick-buy-drawer';

  class DiscoverQuickBuy extends HeaderCard {
    get anchorSelector() {
      return '.header__capsule--tools';
    }

    triggers() {
      return [];
    }

    headIcon() {
      return this.querySelector('.dsc-panel__title svg');
    }

    triggerIcon() {
      return this._fromIcon || null;
    }

    wire() {
      this.contentEl = this.querySelector('[data-quick-buy-content]');
      this.loadingEl = this.querySelector('[data-quick-buy-loading]');
      this.errorEl = this.querySelector('[data-quick-buy-error]');
      this.linkEl = this.querySelector('[data-quick-buy-link]');
      this.backEl = this.querySelector('[data-action="back"]');
      this._from = null;
      this._fromIcon = null;
      this._loadKey = 0;
      // A licence added from the form inside: close, the cart takes over
      this.addEventListener('variant:added', () => {
        this._from = null;
        this.open = false;
      });
    }

    /** Open for a product, remembering which card to return to */
    async openFor(productUrl, from) {
      const url = String(productUrl || '').split('?')[0].split('#')[0];
      if (!/^\/(?:collections\/[^/]+\/)?products\/[a-z0-9-]+$/i.test(url)) return;
      this._from = from === 'cart' || from === 'wishlist' ? from : null;
      // The bag icon slides in from the card that had it (the cart's head)
      const origin = this._from === 'cart' ? document.getElementById(CART_ID) : null;
      this._fromIcon = origin ? origin.querySelector('.drawer__title svg') : null;
      if (this.backEl) this.backEl.hidden = !this._from;
      if (this.linkEl) this.linkEl.setAttribute('href', url);
      this.errorEl.hidden = true;
      this.contentEl.innerHTML = '';
      this.contentEl.hidden = true;
      this.loadingEl.hidden = false;
      this.open = true;
      const key = ++this._loadKey;
      try {
        const response = await fetch(`${url}?view=${QUICK_BUY_VIEW}`, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        if (key !== this._loadKey) return; // another product opened meanwhile
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('quick-buy-drawer .drawer__content');
        if (!content) throw new Error('No quick-buy content');
        content.querySelectorAll('cart-notification, script:not([type="application/json"])').forEach((el) => el.remove());
        this.loadingEl.hidden = true;
        this.contentEl.innerHTML = content.innerHTML;
        this.contentEl.hidden = false;
        if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
        this.querySelector('.dsc-panel__body').scrollTop = 0;
      } catch {
        if (key !== this._loadKey) return;
        this.loadingEl.hidden = true;
        this.errorEl.hidden = false;
      }
    }

    onClick(event) {
      if (event.target.closest('[data-action="back"]')) {
        event.preventDefault();
        this.goBack();
        return true;
      }
      return super.onClick(event);
    }

    onEscape() {
      this.goBack();
    }

    goBack() {
      const from = this._from;
      this._from = null;
      if (this.open) this.open = false;
      if (from === 'cart') {
        const cart = document.getElementById(CART_ID);
        if (cart) cart.open = true;
      } else if (from === 'wishlist') {
        const wishlist = document.getElementById('wishlist-panel');
        if (wishlist) wishlist.open = true;
      }
    }

    onClose() {
      this._loadKey++; // drop an in-flight load
      this._fromIcon = null;
    }
  }

  window.customElements.define('discover-quick-buy', DiscoverQuickBuy);

  // Add controls anywhere (cart rows re-rendered by Focal, wishlist rows drawn client-side)
  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-quick-buy]');
    if (!control) return;
    const card = document.getElementById('quick-buy-panel');
    if (!card || typeof card.openFor !== 'function') return; // no card: the row's links still work
    event.preventDefault();
    track('Discover Panel Clicked', {
      lane: control.getAttribute('data-lane') || 'cart_rec_add',
      position: Number(control.getAttribute('data-position')) || 0,
      term: null,
      product_id: control.getAttribute('data-product-id') || control.closest('[data-product-id]')?.getAttribute('data-product-id') || null,
      query_length: 0,
    });
    card.openFor(control.getAttribute('data-quick-buy'), control.getAttribute('data-quick-buy-from'));
  });

  /* ============================================================
     Cart card — Focal's cart drawer (#mini-cart, sections/mini-cart.liquid)
     restyled as a header card on lap and up (discover.css). Every piece of
     the drawer stays as it was: the section re-render on cart:refresh,
     line items and quantities, discount progress, Pixels, the
     recommendations row, the order note, checkout. This companion only
     gives the drawer the header-card geometry (over the tools capsule at
     its width, like the wishlist card) and keeps it exclusive with the
     search and wishlist cards. Below 1000px it opens from the one compact
     capsule the same way (Sep 2026).
     ============================================================ */

  /* ============================================================
     Nav Clicked — one event for every navigation surface built Sep 2026
     (desktop row, mega-menu panels and tiles, the compact menu card's
     rows, chips and tiles), so nav usage is one insight broken down by
     `surface` instead of autocapture element chains. Autocapture still
     fires alongside; this adds the names. Delegated on the document, so
     the theme editor's re-rendered header needs no rewiring.
     ============================================================ */

  (function wireNavTracking() {
    const text = (el) => (el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '');
    const itemOf = (el) => {
      const li = el.closest('.header__linklist-item[data-item-title], .mobile-nav__item[data-level="1"][data-item-title]');
      return li ? li.getAttribute('data-item-title') : null;
    };
    const groupOf = (el) => {
      const column = el.closest('.mega-menu__column');
      if (column) return text(column.querySelector('.mega-menu__title')) || null;
      const group = el.closest('.mobile-nav__group');
      return group ? text(group.querySelector('.mobile-nav__group-title')) || null : null;
    };
    /** [surface, label, extra props] for a clicked element, or null when it is not a nav control */
    const classify = (target) => {
      const inMenuCard = !!target.closest('#mobile-menu-drawer');
      const inMega = !!target.closest('.mega-menu');
      const tile = target.closest('.mega-menu__tiles-item, .mobile-nav__tiles-item');
      if (tile) return [inMenuCard ? 'menu_card_tile' : 'mega_tile', tile.getAttribute('aria-label') || text(tile), tile, { product_id: tile.getAttribute('data-product-id') || null }];
      const more = target.closest('.mega-menu__tiles-more, .mobile-nav__tiles-more');
      if (more) return [inMenuCard ? 'menu_card_view_all' : 'mega_view_all', text(more), more, {}];
      const chip = target.closest('.mobile-nav__chip');
      if (chip) return ['menu_card_chip', text(chip), chip, {}];
      const groupLink = target.closest('a.mega-menu__title, a.mobile-nav__group-title');
      if (groupLink) return [inMenuCard ? 'menu_card_group' : 'mega_group', text(groupLink), groupLink, {}];
      const panelLink = inMega ? target.closest('.mega-menu__column a') : null;
      if (panelLink) return ['mega_panel', text(panelLink), panelLink, {}];
      const row = target.closest('.mobile-nav__item[data-level="1"] > .mobile-nav__link');
      if (row) {
        const expands = row.tagName === 'BUTTON';
        return ['menu_card_row', text(row), row, { action: expands ? (row.getAttribute('aria-expanded') === 'true' ? 'collapse' : 'expand') : 'navigate' }];
      }
      const topLink = target.closest('a.header__linklist-link');
      if (topLink) return ['desktop_row', text(topLink), topLink, {}];
      return null;
    };
    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        const hit = classify(event.target);
        if (!hit) return;
        const [surface, label, el, extra] = hit;
        track('Nav Clicked', {
          surface,
          item: itemOf(el),
          group: groupOf(el),
          label,
          href: el.getAttribute('href') || null,
          path: window.location.pathname,
          ...extra,
        });
      },
      true
    );
  })();

  /* ============================================================
     Menu card — Focal's navigation drawer (#mobile-menu-drawer,
     snippets/mobile-menu.liquid) as a header card wherever the hamburger
     shows (below 1200px): the cart card's treatment, geometry from the
     nav capsule on lap and from the one compact capsule below 1000px
     (discover.css). Focal's drawer element keeps its own open/close,
     focus trap and scroll lock; the accordion inside is untouched. Its
     own html class (dsc-menu-open) fades the capsule it hangs from.
     ============================================================ */

  const MENU_ID = 'mobile-menu-drawer';

  function closeMenuCard() {
    const menu = document.getElementById(MENU_ID);
    if (menu && menu.hasAttribute('open')) menu.open = false;
  }

  (function wireMenuCard() {
    const wide = window.matchMedia(PANEL_WIDE);
    const openMenu = () => {
      const menu = document.getElementById(MENU_ID);
      return menu && menu.hasAttribute('open') ? menu : null;
    };
    const place = (menu) => applyHeaderGeometry(menu, wide.matches ? '.header__capsule--nav' : COMPACT_ANCHOR);
    // The hamburger's slide into the card's title needs its offset before `open` lands (capture, ahead of Focal)
    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest(`[aria-controls="${MENU_ID}"]`);
        const menu = document.getElementById(MENU_ID);
        if (!trigger || !menu || menu.hasAttribute('open')) return;
        document.documentElement.classList.add('lock-all');
        place(menu);
        stageCardIcon(menu, menu.querySelector('.drawer__title svg'), trigger.querySelector('svg'));
        menu._dscStaged = true;
      },
      true
    );
    document.addEventListener('openable-element:open', (event) => {
      if (!event.target || event.target.id !== MENU_ID) return;
      openCards.forEach((card) => {
        card.open = false;
      });
      closeCartCard();
      document.documentElement.classList.add('lock-all');
      place(event.target);
      if (!event.target._dscStaged) stageCardIcon(event.target, null, null);
      event.target._dscStaged = false;
      document.documentElement.classList.add('dsc-menu-open');
      track('Menu Card Opened', { path: window.location.pathname, wide: wide.matches });
    });
    document.addEventListener('openable-element:close', (event) => {
      if (!event.target || event.target.id !== MENU_ID) return;
      document.documentElement.classList.remove('dsc-menu-open');
    });
    window.addEventListener('resize', () => {
      const menu = openMenu();
      if (menu) place(menu);
    });
    wide.addEventListener('change', () => {
      const menu = openMenu();
      if (menu) menu.open = false;
    });
  })();

  const CART_ID = 'mini-cart';

  function closeCartCard() {
    const cart = document.getElementById(CART_ID);
    if (cart && cart.hasAttribute('open')) cart.open = false;
  }

  (function wireCartCard() {
    const wide = window.matchMedia(PANEL_WIDE);
    const openCart = () => {
      const cart = document.getElementById(CART_ID);
      return cart && cart.hasAttribute('open') ? cart : null;
    };
    const place = (cart) => applyHeaderGeometry(cart, wide.matches ? '.header__capsule--tools' : COMPACT_ANCHOR);
    // The bag icon's slide needs its offset before `open` lands, so it is
    // staged on the trigger's click (capture, ahead of Focal's toggle-link).
    // An open from add-to-cart has no trigger click: the icon just fades in.
    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest(`[aria-controls="${CART_ID}"]`);
        const cart = document.getElementById(CART_ID);
        if (!trigger || !cart || cart.hasAttribute('open')) return;
        document.documentElement.classList.add('lock-all');
        place(cart);
        stageCardIcon(cart, cart.querySelector('.drawer__title svg'), trigger.querySelector('svg'));
        cart._dscStaged = true;
      },
      true
    );
    // Focal fires this (bubbling) from the drawer's attributeChangedCallback
    // as soon as `open` lands, before it adds lock-all
    document.addEventListener('openable-element:open', (event) => {
      if (!event.target || event.target.id !== CART_ID) return;
      openCards.forEach((card) => {
        card.open = false;
      });
      closeMenuCard();
      // Lock first so the measurement matches the scrollbar-less layout
      // (DrawerContent toggles the same class right after this event)
      document.documentElement.classList.add('lock-all');
      place(event.target);
      if (!event.target._dscStaged) stageCardIcon(event.target, null, null);
      event.target._dscStaged = false;
      document.documentElement.classList.add('dsc-card-open');
    });
    document.addEventListener('openable-element:close', (event) => {
      if (!event.target || event.target.id !== CART_ID) return;
      document.documentElement.classList.remove('dsc-card-open');
    });
    window.addEventListener('resize', () => {
      const cart = openCart();
      if (cart) place(cart);
    });
    // Crossing the breakpoint swaps card for drawer: close and restart
    wide.addEventListener('change', () => {
      const cart = openCart();
      if (cart) cart.open = false;
    });
  })();

  /* Stale-while-revalidate for the filter rail: every response's facets are
     snapshotted so the next page load paints a real rail before the network
     answers. Public catalog data only, a few KB, always overwritten by the
     live response. Bump the key suffix if the facet payload shape changes. */
  const FACET_SNAPSHOT_KEY = 'dsc:facets:v2';

  /* Whitelists mirror the backend validator — an unknown URL value must not
     400 the search */
  const SORT_OPTIONS = ['relevance', 'newest', 'price_asc', 'price_desc', 'popular', 'best_selling'];
  const SORT_LABELS = [
    ['relevance', 'Relevance'],
    ['newest', 'Newest'],
    ['price_asc', 'Price: Low to High'],
    ['price_desc', 'Price: High to Low'],
    ['popular', 'Trending'],
    ['best_selling', 'Best Sellers'],
  ];
  const PRICE_BUCKETS = [
    ['free', 'Free'],
    ['under15', 'Under $15'],
    ['15to25', '$15–$25'],
    ['25plus', '$25+'],
  ];
  const PRICE_BUCKET_LABELS = Object.fromEntries(PRICE_BUCKETS);

  function readFacetSnapshot() {
    try {
      const parsed = JSON.parse(localStorage.getItem(FACET_SNAPSHOT_KEY));
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  function saveFacetSnapshot(facets) {
    try {
      localStorage.setItem(FACET_SNAPSHOT_KEY, JSON.stringify(facets));
    } catch {
      /* private mode / quota — the snapshot is an enhancement only */
    }
  }

  /* ============================================================
     <discover-search-page> — the /search experience
     URL is the source of truth: q, category, styles, moods,
     use_cases, types, works_with, price_bucket, webfonts,
     on_sale, sort, creator. JS reads it, fetches, renders, and
     writes changes back with pushState.
     ============================================================ */

  class DiscoverSearchPage extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.form = this.querySelector('[data-dsc-form]');
      this.input = this.querySelector('[data-dsc-input]');
      this.chipsEl = this.querySelector('[data-dsc-chips]');
      this.amiEl = this.querySelector('[data-dsc-ami]');
      this.imageStripEl = this.querySelector('[data-dsc-image-strip]');
      this.dymEl = this.querySelector('[data-dsc-dym]');
      this.facetsEl = this.querySelector('[data-dsc-facets]');
      this.sheetEl = this.querySelector('[data-dsc-sheet]');
      this.sheetBodyEl = this.querySelector('[data-dsc-sheet-body]');
      this.metaEl = this.querySelector('[data-dsc-meta]');
      this.gridEl = this.querySelector('[data-dsc-grid]');
      this.moreEl = this.querySelector('[data-dsc-more]');
      this.zeroEl = this.querySelector('[data-dsc-zero]');
      this.errorEl = this.querySelector('[data-dsc-error]');
      this.fileInput = this.querySelector('[data-dsc-file]');
      this.resultsEl = this.querySelector('.dsc-results');

      this.state = this.readUrl();
      this.page = 1;
      this.lastResult = null;
      this.intent = null;
      // Analytics id of the search whose results are on screen; result
      // clicks beacon it. Image mode keeps its own on imageMode.queryId.
      this.queryId = null;
      this.lastSource = 'direct';
      // Image search is its own lane: { phase, thumbUrl, profile, styles,
      // description, items, error }. It owns the results while active and
      // survives text edits — only its own controls, a facet click, or a new
      // text-search submit end it. The parked text state is restored on exit.
      this.imageMode = null;
      this.imageIntro = false;
      this._searchSeq = 0;

      this.wireEvents();
      this.wireRailHeight();
      // Homepage camera lands here with ?image=1: open the intro once the
      // (URL-cleaning) initial search has started, never on back/forward.
      const landing = new URLSearchParams(window.location.search);
      const openImage = landing.get('image') === '1';
      // Entry-point attribution rides in on the URL from the hero, header
      // and pills; the URL rewrite below strips it so a shared link is clean.
      // The back button and a reload land here too (the page is rarely in
      // bfcache): those are returns to a search already logged, not new ones.
      const navType = navigationType();
      const landingSource =
        navType === 'back_forward' ? 'back' : navType === 'reload' ? 'reload' : landing.get('src') || 'direct';
      // Cold-landing marker for the stealth crawler fleets (Aug 2026): a
      // navigate straight onto /search?q=... with no referrer and no prior
      // Discover state on the device. The Sep 2026 variant harvests the
      // product-page tag links WITH their &src=tags param, so the src check
      // that used to sit here let ~150 rows/day through unflagged; a real
      // visitor arriving via any src always carries a same-origin referrer,
      // so no src is exempt (mirrors the posthog.liquid gate). The flag rides
      // to the API once so the analytics row can be marked suspected_bot.
      // Must be read before sessionId() runs, which creates dsc_session_id.
      let priorState = false;
      try {
        priorState = !!window.localStorage.getItem('dsc_session_id');
      } catch {}
      this._coldLanding = !!landing.get('q') && !document.referrer && !priorState;
      this.runSearch({ replaceUrl: true, source: landingSource });
      if (openImage) {
        this.toggleImageIntro(true);
        const pick = this.imageStripEl.querySelector('[data-dsc-image-pick]');
        if (pick) pick.focus();
      }
    }

    /* The filter rail must always end at the bottom of the window — in flow
       (top below the search band) and pinned (top at the sticky offset).
       CSS can't measure an element's live viewport offset, so a watcher
       writes it to a custom prop the rail's max-height calc reads. */
    wireRailHeight() {
      const update = () => {
        this._railRaf = 0;
        const top = this.facetsEl.getBoundingClientRect().top;
        this.facetsEl.style.setProperty('--dsc-rail-top', `${Math.max(0, Math.round(top))}px`);
      };
      const schedule = () => {
        if (this._railRaf) return;
        this._railRaf = requestAnimationFrame(update);
      };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(schedule);
        // Anything that reflows above the rail without a scroll event —
        // band chips rendering, the sticky header expanding back at top
        const band = this.querySelector('[data-dsc-band]');
        if (band) observer.observe(band);
        observer.observe(document.body);
      }
      update();
    }

    /* ----- URL state ----- */

    readUrl() {
      const params = new URLSearchParams(window.location.search);
      const list = (key) =>
        (params.get(key) || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
          .slice(0, 6); // same cap as applyFacet — an overlong URL must not 400 the search
      const bucket = params.get('price_bucket');
      const sort = params.get('sort');
      // Array check, not an object lookup — PRICE_BUCKET_LABELS['constructor']
      // is truthy and would smuggle a prototype key into the request
      const validBucket = PRICE_BUCKETS.some(([key]) => key === bucket);
      return {
        q: (params.get('q') || '').slice(0, 200),
        category: params.get('category') || null,
        styles: list('styles'),
        moods: list('moods'),
        use_cases: list('use_cases'),
        types: list('types'),
        works_with: list('works_with'),
        // free=true is the pre-price-group URL shape — fold it into the bucket
        price_bucket: validBucket ? bucket : params.get('free') === 'true' ? 'free' : null,
        webfonts: params.get('webfonts') === 'true',
        on_sale: params.get('on_sale') === 'true',
        sort: SORT_OPTIONS.includes(sort) && sort !== 'relevance' ? sort : null,
        creator: params.get('creator') || null,
      };
    }

    writeUrl(push) {
      // Start from the live query string so non-Discover params (utm_*,
      // preview params, app-added keys) survive our rewrites
      const params = new URLSearchParams(window.location.search);
      // 'free' is legacy (folded into price_bucket) and 'image' is the hero's
      // one-shot camera handoff — both always cleaned up
      ['q', 'category', 'styles', 'moods', 'use_cases', 'types', 'works_with', 'price_bucket', 'webfonts', 'on_sale', 'sort', 'free', 'creator', 'image', 'src'].forEach(
        (key) => params.delete(key)
      );
      if (this.state.q) params.set('q', this.state.q);
      if (this.state.category) params.set('category', this.state.category);
      if (this.state.styles.length) params.set('styles', this.state.styles.join(','));
      if (this.state.moods.length) params.set('moods', this.state.moods.join(','));
      if (this.state.use_cases.length) params.set('use_cases', this.state.use_cases.join(','));
      if (this.state.types.length) params.set('types', this.state.types.join(','));
      if (this.state.works_with.length) params.set('works_with', this.state.works_with.join(','));
      if (this.state.price_bucket) params.set('price_bucket', this.state.price_bucket);
      if (this.state.webfonts) params.set('webfonts', 'true');
      if (this.state.on_sale) params.set('on_sale', 'true');
      if (this.state.sort) params.set('sort', this.state.sort);
      if (this.state.creator) params.set('creator', this.state.creator);
      const qs = params.toString();
      const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      if (push) {
        history.pushState({ dsc: true }, '', url);
      } else {
        history.replaceState({ dsc: true }, '', url);
      }
    }

    /* ----- events ----- */

    wireEvents() {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.state.q = this.input.value.trim().slice(0, 200);
        this.clearImageMode();
        this.runSearch({ pushUrl: true, source: 'typed' });
      });

      this.input.addEventListener('input', () => {
        this.form.classList.toggle('has-value', this.input.value.length > 0);
      });
      this.form.classList.toggle('has-value', this.input.value.length > 0);

      const clearBtn = this.querySelector('[data-dsc-clear]');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => {
          this.input.value = '';
          this.state.q = '';
          this.form.classList.remove('has-value');
          if (this.imageMode) {
            // Image mode owns the results — dropping the parked text must
            // not drop the image. Sync the URL so exiting lands on q=''.
            this.writeUrl(false);
          } else {
            this.runSearch({ pushUrl: true, source: 'typed' });
          }
          this.input.focus();
        });
      }

      window.addEventListener('popstate', () => {
        this.state = this.readUrl();
        this.input.value = this.state.q;
        this.clearImageMode();
        this.runSearch({ source: 'back' });
      });

      // Delegated clicks: facets, chips, load more, retries
      this.addEventListener('click', (event) => this.onClick(event));

      // Mobile facet sheet
      const filtersBtn = this.querySelector('[data-dsc-filters-btn]');
      if (filtersBtn) filtersBtn.addEventListener('click', () => this.toggleSheet(true));

      // Image search: intro panel, file picker, drag-drop, paste
      const imageBtn = this.querySelector('[data-dsc-image-btn]');
      if (imageBtn && this.fileInput) {
        // Straight to the picker — no explainer in between. In image mode
        // the same button means "swap the image".
        imageBtn.addEventListener('click', () => this.fileInput.click());
        this.fileInput.addEventListener('change', () => {
          if (this.fileInput.files && this.fileInput.files[0]) {
            this.searchByImage(this.fileInput.files[0]);
            this.fileInput.value = '';
          }
        });
      }
      const band = this.querySelector('[data-dsc-band]');
      if (band) {
        ['dragenter', 'dragover'].forEach((type) =>
          band.addEventListener(type, (event) => {
            event.preventDefault();
            band.classList.add('is-dragover');
          })
        );
        ['dragleave', 'drop'].forEach((type) =>
          band.addEventListener(type, (event) => {
            event.preventDefault();
            band.classList.remove('is-dragover');
          })
        );
        band.addEventListener('drop', (event) => {
          const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
          if (file && file.type.startsWith('image/')) this.searchByImage(file);
        });
      }
      document.addEventListener('paste', (event) => {
        if (!event.clipboardData) return;
        // A screenshot pasted into the brief canvas belongs to the brief,
        // not to image search
        const brief = document.querySelector('discover-brief');
        if (brief && brief.classList.contains('is-open')) return;
        const file = Array.from(event.clipboardData.files || []).find((entry) =>
          entry.type.startsWith('image/')
        );
        if (file) this.searchByImage(file);
      });

      // Brief mode is off for launch: the section renders no button. The
      // hook stays so re-adding the markup is all it takes.
      const briefBtn = this.querySelector('[data-dsc-brief-btn]');
      if (briefBtn) {
        briefBtn.addEventListener('click', () => {
          const canvas = document.querySelector('discover-brief');
          if (canvas) canvas.openCanvas(this.state.q);
        });
      }

      this.wireResultClicks();
    }

    /* One delegated listener: which card, at which position, from which
       search. Beaconed to Postgres (ranking evidence) and mirrored to
       PostHog (funnels). The product page reads the remembered click. */
    wireResultClicks() {
      this.gridEl.addEventListener('click', (event) => {
        // Only a link to the product counts: cards also carry the vendor
        // link, quick-buy and wishlist controls
        const link = event.target.closest('a[href]');
        if (!link || !/^\/products\//.test(link.getAttribute('href') || '')) return;
        const card = link.closest('.product-item[data-product-id]');
        if (!card || !this.gridEl.contains(card)) return;
        const queryId = this.imageMode ? this.imageMode.queryId : this.queryId;
        if (!queryId) return;
        const cards = Array.from(this.gridEl.querySelectorAll('.product-item[data-product-id]'));
        const productId = card.getAttribute('data-product-id');
        const click = {
          query_id: queryId,
          shopify_product_id: productId,
          position: cards.indexOf(card),
          // Stamped when the card was appended; load-more dedupes, so the
          // grid index cannot be turned back into a page
          page: Number(card.getAttribute('data-dsc-page')) || 1,
        };
        postBeacon('/click', { ...click, session_id: sessionId() });
        rememberClick(click);
        track(
          'Discover Result Clicked',
          { query_id: queryId, product_id: productId, position: click.position, page: click.page },
          { send_instantly: true }
        );
      });
    }

    onClick(event) {
      const facet = event.target.closest('[data-facet-key]');
      if (facet) {
        this.applyFacet(
          facet.getAttribute('data-facet-key'),
          facet.getAttribute('data-facet-value'),
          facet.hasAttribute('data-dsc-pivot') ? 'pivot' : 'facet'
        );
        return;
      }
      const group = event.target.closest('[data-facet-group-toggle]');
      if (group) {
        const wrapper = group.closest('.dsc-facet-group');
        if (wrapper.hasAttribute('data-collapsed')) {
          wrapper.removeAttribute('data-collapsed');
        } else {
          wrapper.setAttribute('data-collapsed', '');
        }
        return;
      }
      const pill = event.target.closest('[data-remove-key]');
      if (pill) {
        this.removeFilter(pill.getAttribute('data-remove-key'), pill.getAttribute('data-remove-value'));
        return;
      }
      const dym = event.target.closest('[data-dsc-dym-q]');
      if (dym) {
        event.preventDefault();
        this.input.value = dym.getAttribute('data-dsc-dym-q');
        this.state.q = this.input.value.slice(0, 200);
        this.trackRefine('dym', 'q', this.state.q);
        this.runSearch({ pushUrl: true, source: 'dym' });
        return;
      }
      if (event.target.closest('[data-dsc-load-more]')) {
        this.loadMore();
        return;
      }
      if (event.target.closest('[data-dsc-retry]')) {
        this.runSearch({ source: this.lastSource });
        return;
      }
      if (event.target.closest('[data-dsc-ami-cta]')) {
        openAmi(this.state.q, this.lastResult ? this.lastResult.total : 0, this.queryId);
        return;
      }
      if (event.target.closest('[data-dsc-image-clear]')) {
        this.clearImageMode();
        // Returns to the parked text results the server already logged
        this.runSearch({ source: 'back' });
        return;
      }
      if (event.target.closest('[data-dsc-image-pick]') || event.target.closest('[data-dsc-image-new]')) {
        this.fileInput.click();
        return;
      }
      if (event.target.closest('[data-dsc-image-intro-close]')) {
        this.toggleImageIntro(false);
        return;
      }
      if (event.target.closest('[data-dsc-image-rematch]')) {
        // A held profile re-matches at search cost; a failed analyze re-runs
        // the vision pass on the same file
        if (this.imageMode && !this.imageMode.profile && this.imageMode.file) {
          this.searchByImage(this.imageMode.file);
        } else {
          this.matchImageProfile();
        }
        return;
      }
      if (event.target.closest('[data-dsc-clear-all]')) {
        // Sort survives — it's an ordering preference, not a filter
        this.state = {
          q: '',
          category: null,
          styles: [],
          moods: [],
          use_cases: [],
          types: [],
          works_with: [],
          price_bucket: null,
          webfonts: false,
          on_sale: false,
          sort: this.state.sort,
          creator: null,
        };
        this.input.value = '';
        this.clearImageMode();
        this.trackRefine('chip_removed', 'all', null);
        this.runSearch({ pushUrl: true, source: 'chip_removed' });
        return;
      }
      // .dsc-sheet__overlay is the FACET DRAWER's overlay (not the brand
      // sheet's) — the round-2 dsc-sheet→dsc-brandsheet rename must never
      // touch this line
      if (event.target.closest('[data-dsc-sheet-close]') || event.target.classList.contains('dsc-sheet__overlay')) {
        this.toggleSheet(false);
      }
    }

    applyFacet(key, value, source = 'facet') {
      this.trackRefine(source, key, value);
      this.clearImageMode();
      if (key === 'category') {
        this.state.category = this.state.category === value ? null : value;
      } else if (key === 'price_bucket' || key === 'free') {
        // 'free' is the legacy key intent chips still emit — it lives in the
        // price group now. Radio semantics: same value toggles off.
        const bucket = key === 'free' ? 'free' : value;
        this.state.price_bucket = this.state.price_bucket === bucket ? null : bucket;
      } else if (key === 'webfonts') {
        this.state.webfonts = !this.state.webfonts;
      } else if (key === 'on_sale') {
        this.state.on_sale = !this.state.on_sale;
      } else if (key === 'creator') {
        this.state.creator = this.state.creator === value ? null : value;
      } else {
        const list = this.state[key];
        const index = list.indexOf(value);
        if (index === -1) {
          if (list.length < 6) list.push(value);
        } else {
          list.splice(index, 1);
        }
      }
      this.runSearch({ pushUrl: true, source });
    }

    removeFilter(key, value) {
      this.trackRefine('chip_removed', key, value);
      this.clearImageMode();
      if (key === 'category') this.state.category = null;
      else if (key === 'price_bucket' || key === 'free') this.state.price_bucket = null;
      else if (key === 'webfonts') this.state.webfonts = false;
      else if (key === 'on_sale') this.state.on_sale = false;
      else if (key === 'creator') this.state.creator = null;
      else if (key === 'q') {
        this.state.q = '';
        this.input.value = '';
      } else {
        const list = this.state[key];
        const index = list.indexOf(value);
        if (index !== -1) list.splice(index, 1);
      }
      this.runSearch({ pushUrl: true, source: 'chip_removed' });
    }

    /* A refinement is an action on the search already on screen, so it
       carries that search's id, not the one it is about to create */
    trackRefine(action, key, value) {
      track('Discover Refined', {
        query_id: this.imageMode ? this.imageMode.queryId : this.queryId,
        action,
        key,
        value,
      });
    }

    toggleSheet(open) {
      if (!this.sheetEl) return;
      this.sheetEl.classList.toggle('is-open', open);
      document.documentElement.style.overflow = open ? 'hidden' : '';
      if (open) this.renderFacets(); // sheet body mirrors the rail
    }

    /* ----- search ----- */

    /* Filter/sort state → request params, shared by first-page and load-more */
    stateParams(params) {
      if (this.state.q) params.q = this.state.q;
      if (this.state.category) params.category = this.state.category;
      if (this.state.styles.length) params.styles = this.state.styles.join(',');
      if (this.state.moods.length) params.moods = this.state.moods.join(',');
      if (this.state.use_cases.length) params.use_cases = this.state.use_cases.join(',');
      if (this.state.types.length) params.types = this.state.types.join(',');
      if (this.state.works_with.length) params.works_with = this.state.works_with.join(',');
      if (this.state.price_bucket) params.price_bucket = this.state.price_bucket;
      if (this.state.webfonts) params.webfonts = 'true';
      if (this.state.on_sale) params.on_sale = 'true';
      if (this.state.sort) params.sort = this.state.sort;
      if (this.state.creator) params.creator = this.state.creator;
      return params;
    }

    async runSearch({ pushUrl, replaceUrl, source = 'direct' } = {}) {
      if (this.imageMode) return; // image mode renders its own results
      if (this.imageIntro) this.toggleImageIntro(false);
      const seq = ++this._searchSeq;
      this.page = 1;
      this.lastSource = source;
      if (pushUrl) this.writeUrl(true);
      else if (replaceUrl) this.writeUrl(false);

      this.showError(false);
      this.showSkeletons();

      const params = this.stateParams({ per_page: '60', session_id: sessionId(), src: source });
      if (this._coldLanding) {
        params.cold = 'true';
        this._coldLanding = false;
      }

      let result;
      const startedAt = performance.now();
      try {
        result = await apiJSON('/search', { params });
      } catch {
        if (seq !== this._searchSeq) return;
        this.showError(true);
        return;
      }
      if (seq !== this._searchSeq) return;

      this.lastResult = result;
      // Refinements are not logged server-side (they are actions on the
      // search already on screen), so clicks keep attributing to it. A
      // return to earlier results recalls the id the server gave them.
      if (result.query_id) this.queryId = result.query_id;
      else if (REFINEMENT_SOURCES.includes(source)) this.queryId = this.queryId || recallQueryId(this.state.q);
      else if (HISTORY_SOURCES.includes(source)) this.queryId = recallQueryId(this.state.q);
      else this.queryId = null;
      rememberQueryId(this.state.q, this.queryId);
      this.trackSearch(result, source, Math.round(performance.now() - startedAt));
      this.normalizeStyleState();
      this.renderMeta();
      this.renderFacets();
      this.renderChips();
      this.renderDym();
      this.renderAmiBand();
      this.fetchIntent(); // intent chips matter most on thin/zero results

      const items = result.items || [];
      if (items.length === 0) {
        this.gridEl.innerHTML = '';
        this.renderZero();
        this.moreEl.hidden = true;
        return;
      }
      this.zeroEl.hidden = true;

      const cards = await renderCards(items);
      if (seq !== this._searchSeq) return;
      if (cards.length === 0) {
        // Ranked ids exist but the section renderer produced nothing —
        // surface the failure instead of an empty page
        this.showError(true);
        return;
      }
      this.gridEl.innerHTML = '';
      cards.forEach((card) => {
        card.setAttribute('data-dsc-page', '1');
        this.gridEl.appendChild(card);
      });
      syncWishlistHearts();
      this.updateMore();
    }

    trackSearch(result, source, roundTripMs) {
      // A query-less browse writes no row and has no id; the funnel counts
      // searches, so it gets no event either. Nor does a return to results
      // already counted (back, reload, popstate).
      if (!this.state.q || HISTORY_SOURCES.includes(source)) return;
      rememberRecentSearch(this.state.q); // feeds the header panel's Recent row
      const props = {
        query_id: result.query_id || null,
        query: this.state.q,
        intent: result.ask_ami ? 'question' : 'keyword',
        source,
        result_count: result.total,
        zero_results: result.total === 0,
        round_trip_ms: roundTripMs,
        sort: this.state.sort || 'relevance',
        filters: this.activeFilterKeys(),
        lanes: result.lanes || null,
      };
      track('Discover Search', props);
      if (result.total === 0) {
        track('Discover Zero Results', { query_id: props.query_id, query: props.query, source });
      }
    }

    /* Which filter groups are active, not their values: enough for a
       breakdown, without pushing every label into event properties */
    activeFilterKeys() {
      const keys = [];
      ['category', 'styles', 'moods', 'use_cases', 'types', 'works_with', 'price_bucket', 'creator'].forEach(
        (key) => {
          const value = this.state[key];
          if (Array.isArray(value) ? value.length > 0 : !!value) keys.push(key);
        }
      );
      if (this.state.webfonts) keys.push('webfonts');
      if (this.state.on_sale) keys.push('on_sale');
      return keys;
    }

    async loadMore() {
      const result = this.lastResult;
      if (!result) return;
      const seq = this._searchSeq;
      const nextPage = this.page + 1;
      const button = this.moreEl.querySelector('button');
      if (button) button.disabled = true;

      const params = this.stateParams({
        per_page: String(result.per_page),
        page: String(nextPage),
        session_id: sessionId(),
      });

      try {
        const pageResult = await apiJSON('/search', { params });
        if (seq !== this._searchSeq) return;
        const pageItems = pageResult.items || [];
        if (pageItems.length === 0) {
          // Pool exhausted (or drifted under us) — retire the button
          this.moreEl.hidden = true;
          return;
        }
        // Later pages are fresh searches whose ranking can drift (trending
        // refresh) — dedupe against what is already on the page
        const seen = new Set(
          Array.from(this.gridEl.querySelectorAll('.product-item[data-product-id]')).map((node) =>
            node.getAttribute('data-product-id')
          )
        );
        const cards = await renderCards(pageItems);
        if (seq !== this._searchSeq) return;
        cards.forEach((card) => {
          if (seen.has(card.getAttribute('data-product-id'))) return;
          card.setAttribute('data-dsc-page', String(nextPage));
          this.gridEl.appendChild(card);
        });
        syncWishlistHearts();
        this.page = nextPage;
        this.lastResult.pool_size = pageResult.pool_size; // fresher bound
        this.updateMore();
      } catch {
        /* leave the button re-enabled for a retry */
      } finally {
        if (button) button.disabled = false;
      }
    }

    updateMore() {
      const result = this.lastResult;
      const served = this.page * (result ? result.per_page : 60);
      // page is validator-capped at 50 — retire the button before a 400
      const hasMore = result && served < result.pool_size && this.page < 50;
      this.moreEl.hidden = !hasMore;
    }

    /* ----- renderers ----- */

    showSkeletons() {
      this.zeroEl.hidden = true;
      this.moreEl.hidden = true;
      const card =
        '<div class="dsc-skeleton-card"><div class="dsc-skeleton"></div><span class="dsc-skeleton-bar"></span><span class="dsc-skeleton-bar dsc-skeleton-bar--short"></span></div>';
      this.gridEl.innerHTML = card.repeat(10);
      // First paint only — later searches keep the previous facets in place
      if (!this.lastResult) this.renderFacetPlaceholder();
    }

    renderFacetSkeletons() {
      let group =
        '<div class="dsc-facet-group dsc-facet-group--skeleton"><span class="dsc-skeleton-bar dsc-skeleton-bar--title"></span>';
      for (let i = 0; i < 5; i += 1) group += '<span class="dsc-skeleton-bar"></span>';
      group += '</div>';
      this.facetsEl.innerHTML = group.repeat(4);
      if (this.sheetBodyEl) this.sheetBodyEl.innerHTML = group.repeat(4);
    }

    showError(show) {
      this.errorEl.hidden = !show;
      if (show) {
        this.gridEl.innerHTML = '';
        this.zeroEl.hidden = true;
        this.moreEl.hidden = true;
        // A first-load failure would otherwise leave the rail shimmering forever
        // (a snapshot rail stays — its filters are real and clickable)
        if (this.facetsEl.querySelector('.dsc-facet-group--skeleton')) {
          this.facetsEl.innerHTML = '';
          if (this.sheetBodyEl) this.sheetBodyEl.innerHTML = '';
        }
      }
    }

    renderMeta() {
      if (this.imageMode) {
        // No Filters button — facets don't apply to visual matches
        const count = (this.imageMode.items || []).length;
        this.metaEl.innerHTML =
          this.imageMode.phase === 'done'
            ? `<span class="dsc-meta__count"><strong>${Number(count).toLocaleString()}</strong> visual ${count === 1 ? 'match' : 'matches'}</span>`
            : '';
        return;
      }
      const result = this.lastResult;
      const count = result ? result.total : 0;
      const activeCount = this.activeFilterCount();
      const currentSort = this.state.sort || 'relevance';
      const sortOptions = SORT_LABELS.map(
        ([value, label]) =>
          `<option value="${value}"${value === currentSort ? ' selected' : ''}>${label}</option>`
      ).join('');
      this.metaEl.innerHTML =
        `<span class="dsc-meta__count"><strong>${Number(count).toLocaleString()}</strong> ${count === 1 ? 'result' : 'results'}</span>` +
        `<label class="dsc-meta__sort"><select data-dsc-sort aria-label="Sort results">${sortOptions}</select>${ICONS.chevron}</label>` +
        `<button type="button" class="dsc-meta__filters-btn" data-dsc-filters-btn>Filters${activeCount > 0 ? `<span class="bubble-count">${activeCount}</span>` : ''}</button>`;
      const filtersBtn = this.metaEl.querySelector('[data-dsc-filters-btn]');
      if (filtersBtn) filtersBtn.addEventListener('click', () => this.toggleSheet(true));
      const sortSelect = this.metaEl.querySelector('[data-dsc-sort]');
      if (sortSelect) {
        sortSelect.addEventListener('change', () => {
          this.state.sort = sortSelect.value === 'relevance' ? null : sortSelect.value;
          this.trackRefine('sort', 'sort', sortSelect.value);
          this.runSearch({ pushUrl: true, source: 'sort' });
        });
      }
    }

    /**
     * A style filter can arrive as a raw label ("grunge" from an old URL)
     * while the facet chip shows the curated name ("Grunge") — normalize
     * state to the canonical spelling so pill and chip agree.
     */
    normalizeStyleState() {
      const options = (this.lastResult && this.lastResult.facets.styles) || [];
      this.state.styles = this.state.styles.map((style) => {
        const lower = style.toLowerCase();
        const match = options.find(
          (option) =>
            (option.curated || option.label).toLowerCase() === lower ||
            option.label.toLowerCase() === lower
        );
        return match ? match.curated || match.label : style;
      });
    }

    activeFilterCount() {
      return (
        (this.state.category ? 1 : 0) +
        this.state.styles.length +
        this.state.moods.length +
        this.state.use_cases.length +
        this.state.types.length +
        this.state.works_with.length +
        (this.state.price_bucket ? 1 : 0) +
        (this.state.webfonts ? 1 : 0) +
        (this.state.on_sale ? 1 : 0) +
        (this.state.creator ? 1 : 0)
      );
    }

    facetGroupHtml(title, key, options, activeTest, { chips } = {}) {
      if (!options || options.length === 0) return '';
      let body = '';
      options.slice(0, 12).forEach((option) => {
        const value = option.curated || option.label;
        const shown = titleLabel(value);
        const active = activeTest(value, option.label);
        if (chips) {
          body += `<button type="button" class="dsc-chip${active ? ' is-active' : ''}" data-facet-key="${key}" data-facet-value="${escapeHtml(value)}" aria-pressed="${active}">${escapeHtml(shown)}</button>`;
        } else {
          body += `<button type="button" class="dsc-facet-option${active ? ' is-active' : ''}" data-facet-key="${key}" data-facet-value="${escapeHtml(value)}"><span class="dsc-facet-option__label">${escapeHtml(shown)}</span><span class="dsc-facet-option__count">${Number(option.count).toLocaleString()}</span></button>`;
        }
      });
      return `<div class="dsc-facet-group"><button type="button" class="dsc-facet-group__title" data-facet-group-toggle>${escapeHtml(title)}${ICONS.chevron}</button><div class="dsc-facet-group__body${chips ? ' dsc-facet-group__body--chips' : ''}">${body}</div></div>`;
    }

    /* Price buckets are radio chips over facets.price_buckets — a fixed
       vocabulary rather than a server label list, so it renders from the
       bucket table, hiding empty buckets unless active. */
    priceGroupHtml(facets) {
      const buckets = facets.price_buckets;
      if (!buckets) return '';
      const state = this.state;
      let body = '';
      PRICE_BUCKETS.forEach(([key, label]) => {
        const count = Number(buckets[key] || 0);
        const active = state.price_bucket === key;
        if (count === 0 && !active) return;
        body += `<button type="button" class="dsc-facet-option${active ? ' is-active' : ''}" data-facet-key="price_bucket" data-facet-value="${key}"><span class="dsc-facet-option__label">${escapeHtml(label)}</span><span class="dsc-facet-option__count">${count.toLocaleString()}</span></button>`;
      });
      if (!body) return '';
      return `<div class="dsc-facet-group"><button type="button" class="dsc-facet-group__title" data-facet-group-toggle>Price${ICONS.chevron}</button><div class="dsc-facet-group__body">${body}</div></div>`;
    }

    /* Single-option toggles that don't warrant a titled group */
    extrasGroupHtml(facets) {
      const state = this.state;
      const toggle = (key, label, count, active) =>
        `<button type="button" class="dsc-facet-option${active ? ' is-active' : ''}" data-facet-key="${key}" data-facet-value="true"><span class="dsc-facet-option__label">${label}</span><span class="dsc-facet-option__count">${Number(count).toLocaleString()}</span></button>`;
      let body = '';
      if (facets.on_sale_count > 0 || state.on_sale) {
        body += toggle('on_sale', 'On sale', facets.on_sale_count || 0, state.on_sale);
      }
      if (facets.webfont_count > 0 || state.webfonts) {
        body += toggle('webfonts', 'Webfonts included', facets.webfont_count || 0, state.webfonts);
      }
      if (!body) return '';
      return `<div class="dsc-facet-group"><div class="dsc-facet-group__body">${body}</div></div>`;
    }

    facetsHtml(facets) {
      const state = this.state;
      let html = '';

      html += this.facetGroupHtml('Category', 'category', facets.categories, (value) => state.category === value);
      html += this.facetGroupHtml('Type', 'types', facets.types, (value) => state.types.includes(value));
      html += this.priceGroupHtml(facets);
      html += this.facetGroupHtml('Styles', 'styles', facets.styles, (value, raw) => state.styles.includes(value) || state.styles.includes(raw), { chips: true });
      html += this.facetGroupHtml('Moods', 'moods', facets.moods, (value) => state.moods.includes(value));
      html += this.facetGroupHtml('Use cases', 'use_cases', facets.use_cases, (value) => state.use_cases.includes(value));
      html += this.facetGroupHtml('Works with', 'works_with', facets.works_with, (value) => state.works_with.includes(value));
      html += this.extrasGroupHtml(facets);
      html += this.facetGroupHtml('Creators', 'creator', facets.creators, (value) => state.creator === value);

      return html;
    }

    renderFacets() {
      if (this.imageMode) return; // the rail is the image panel while active
      const result = this.lastResult;
      if (!result) {
        this.renderFacetPlaceholder();
        return;
      }
      const html = this.facetsHtml(result.facets);
      this.facetsEl.classList.remove('is-stale');
      this.facetsEl.innerHTML = html;
      if (this.sheetBodyEl) {
        this.sheetBodyEl.classList.remove('is-stale');
        this.sheetBodyEl.innerHTML = html;
      }
      saveFacetSnapshot(result.facets);
    }

    /* First paint: a real rail from the last visit's facets (counts dimmed
       until the live response replaces them), skeletons on a cold start */
    renderFacetPlaceholder() {
      if (this.imageMode) return; // the rail is the image panel while active
      const snapshot = readFacetSnapshot();
      if (!snapshot) {
        this.renderFacetSkeletons();
        return;
      }
      const html = this.facetsHtml(snapshot);
      this.facetsEl.classList.add('is-stale');
      this.facetsEl.innerHTML = html;
      if (this.sheetBodyEl) {
        this.sheetBodyEl.classList.add('is-stale');
        this.sheetBodyEl.innerHTML = html;
      }
    }

    renderChips() {
      const state = this.state;
      let html = '';

      const pill = (label, key, value) =>
        `<button type="button" class="dsc-chip is-active" data-remove-key="${key}"${value ? ` data-remove-value="${escapeHtml(value)}"` : ''}>${escapeHtml(titleLabel(label))}<span class="dsc-chip__x">${ICONS.close}</span></button>`;

      if (state.category) html += pill(state.category, 'category');
      state.types.forEach((type) => (html += pill(type, 'types', type)));
      state.styles.forEach((style) => (html += pill(style, 'styles', style)));
      state.moods.forEach((mood) => (html += pill(mood, 'moods', mood)));
      state.use_cases.forEach((useCase) => (html += pill(useCase, 'use_cases', useCase)));
      state.works_with.forEach((app) => (html += pill(app, 'works_with', app)));
      if (state.price_bucket) {
        html += pill(PRICE_BUCKET_LABELS[state.price_bucket] || state.price_bucket, 'price_bucket');
      }
      if (state.webfonts) html += pill('Webfonts', 'webfonts');
      if (state.on_sale) html += pill('On sale', 'on_sale');
      if (state.creator) html += pill(state.creator, 'creator');

      // Style pivots from the result ("More Grunge →")
      const pivots = this.lastResult ? this.lastResult.style_pivots : [];
      pivots.forEach((pivot) => {
        if (state.styles.includes(pivot.name)) return;
        html += `<button type="button" class="dsc-chip" data-facet-key="styles" data-facet-value="${escapeHtml(pivot.name)}" data-dsc-pivot>${escapeHtml(pivot.name)}</button>`;
      });

      // Parsed-intent suggestions (purple) — rendered by fetchIntent()
      if (this.intent) html += this.intentChipsHtml();

      if (this.activeFilterCount() > 1) {
        html += '<button type="button" class="dsc-chip" data-dsc-clear-all>Clear all</button>';
      }

      this.chipsEl.innerHTML = html;
    }

    intentChipsHtml() {
      const intent = this.intent;
      const state = this.state;
      let html = '';
      if (intent.category && intent.category !== state.category) {
        html += `<button type="button" class="dsc-chip dsc-chip--ami" data-facet-key="category" data-facet-value="${escapeHtml(intent.category)}">${escapeHtml(intent.category)}</button>`;
      }
      (intent.styles || []).forEach((style) => {
        if (!state.styles.includes(style)) {
          html += `<button type="button" class="dsc-chip dsc-chip--ami" data-facet-key="styles" data-facet-value="${escapeHtml(style)}">${escapeHtml(style)}</button>`;
        }
      });
      if (intent.use_case && !state.use_cases.includes(intent.use_case)) {
        html += `<button type="button" class="dsc-chip dsc-chip--ami" data-facet-key="use_cases" data-facet-value="${escapeHtml(intent.use_case)}">for ${escapeHtml(displayLabel(intent.use_case))}</button>`;
      }
      if (intent.free === true && state.price_bucket !== 'free') {
        html += '<button type="button" class="dsc-chip dsc-chip--ami" data-facet-key="free" data-facet-value="true">Free</button>';
      }
      return html;
    }

    async fetchIntent() {
      const query = this.state.q;
      if (query === this._intentFor) return; // filter-only change — chips already current
      this._intentFor = query;
      this.intent = null;
      if (!query || query.split(/\s+/).length < 2) {
        this.renderChips();
        return;
      }
      try {
        const data = await apiJSON('/intent', { method: 'POST', body: { query } });
        if (this.state.q !== query) return;
        this.intent = data.intent;
      } catch {
        this.intent = null;
      }
      this.renderChips();
    }

    renderDym() {
      const dym = this.lastResult ? this.lastResult.did_you_mean : null;
      if (dym && this.lastResult.total === 0) {
        this.dymEl.innerHTML = `Did you mean <a href="#" data-dsc-dym-q="${escapeHtml(dym)}">${escapeHtml(dym)}</a>?`;
        this.dymEl.hidden = false;
      } else {
        this.dymEl.hidden = true;
        this.dymEl.innerHTML = '';
      }
    }

    renderAmiBand() {
      const result = this.lastResult;
      if (result && result.ask_ami) {
        this.amiEl.innerHTML =
          '<span class="dsc-ami-band__icon">✦</span>' +
          `<span class="dsc-ami-band__text">Sounds like a question. <strong>Ami</strong> can answer it: licensing, installs, downloads, anything.</span>` +
          '<button type="button" class="dsc-ami-band__cta" data-dsc-ami-cta>Ask Ami</button>';
        this.amiEl.hidden = false;
      } else {
        this.amiEl.hidden = true;
        this.amiEl.innerHTML = '';
      }
    }

    renderZero() {
      const result = this.lastResult;
      let chips = '';
      (result.style_pivots || []).forEach((pivot) => {
        chips += `<button type="button" class="dsc-chip" data-facet-key="styles" data-facet-value="${escapeHtml(pivot.name)}" data-dsc-pivot>${escapeHtml(pivot.name)}</button>`;
      });
      (result.facets.categories || []).slice(0, 4).forEach((category) => {
        chips += `<button type="button" class="dsc-chip" data-facet-key="category" data-facet-value="${escapeHtml(category.label)}">${escapeHtml(category.label)}</button>`;
      });
      chips += '<button type="button" class="dsc-chip" data-dsc-clear-all>Browse everything</button>';

      const title = this.state.q
        ? `Nothing for “${escapeHtml(this.state.q)}” yet`
        : 'Nothing matches those filters';
      this.zeroEl.innerHTML =
        `<h2 class="dsc-zero__title">${title}</h2>` +
        '<p class="dsc-zero__hint">Try fewer words, a style, or a different spelling.</p>' +
        `<div class="dsc-zero__chips">${chips}</div>`;
      this.zeroEl.hidden = false;
    }

    /* ----- image search -----
       A standalone lane in two steps: /image/analyze runs the vision pass
       once per upload and returns a profile; /image/match turns the held
       profile into ranked items. While it runs, the text results stay on
       screen (dimmed) and come back untouched if analysis fails. The facet
       rail becomes the image panel on desktop; the strip carries the same
       state on mobile. */

    async searchByImage(file) {
      if (file.size > 5 * 1024 * 1024) {
        this.imageNotice('That image is over 5MB. Try a smaller one.');
        return;
      }
      const seq = ++this._searchSeq;
      const wasActive = !!this.imageMode;
      if (this.imageIntro) this.toggleImageIntro(false);
      if (wasActive && this.imageMode.thumbUrl) URL.revokeObjectURL(this.imageMode.thumbUrl);
      this.imageMode = { phase: 'reading', thumbUrl: URL.createObjectURL(file), file };
      // Back should exit image search, not the page. The URL is unchanged;
      // popstate clears image mode and re-runs the parked text state.
      if (!wasActive) history.pushState({ dsc: true }, '', window.location.href);
      this.showError(false);
      this.resultsEl.classList.add('is-analyzing');
      this.renderImagePanels();

      const formData = new FormData();
      formData.append('image', file);
      let analysis;
      try {
        analysis = await apiJSON('/image/analyze', { method: 'POST', formData });
      } catch (error) {
        if (seq !== this._searchSeq) return;
        // A backend without the two-step endpoints answers 404 — fall back
        // to the legacy one-shot call
        if (error.status === 404 || / 404$/.test(error.message)) {
          this.legacySearchByImage(formData, seq);
          return;
        }
        // 503: the vision service, not the image. Keep the file for a retry.
        if (error.status === 503) {
          this.imageFail('Image search is busy. Try again in a moment.', { retry: true, outcome: 'busy' });
          return;
        }
        this.imageFail("Couldn't read that image. Try another, or search with words.");
        return;
      }
      if (seq !== this._searchSeq) return;

      const profile = analysis.profile || {};
      const hasSignal =
        (profile.visual_styles || []).length > 0 ||
        (profile.mood || []).length > 0 ||
        (profile.use_cases || []).length > 0 ||
        profile.description;
      if (!hasSignal) {
        this.imageFail(
          "Couldn't make out a style in that image. Try a tighter crop, or search with words.",
          { outcome: 'no_style' }
        );
        return;
      }
      this.imageMode = {
        ...this.imageMode,
        profile,
        description: profile.description || '',
        styles: analysis.styles || [],
      };
      this.matchImageProfile(seq);
    }

    /** One-shot POST /image — kept for a backend deployed before the split */
    async legacySearchByImage(formData, seq) {
      let data;
      try {
        data = await apiJSON('/image', { method: 'POST', formData });
      } catch {
        if (seq !== this._searchSeq) return;
        this.imageFail("Couldn't read that image. Try another, or search with words.");
        return;
      }
      if (seq !== this._searchSeq) return;
      this.finishImageResults(data, seq);
    }

    /** Step 2 — plain search cost: retries never re-run the vision pass. */
    async matchImageProfile(seq = ++this._searchSeq) {
      if (!this.imageMode || !this.imageMode.profile) return;
      this.imageMode.phase = 'matching';
      this.resultsEl.classList.add('is-analyzing');
      this.renderImagePanels();
      let data;
      try {
        data = await apiJSON('/image/match', {
          method: 'POST',
          body: { profile: this.imageMode.profile, session_id: sessionId() },
        });
      } catch {
        if (seq !== this._searchSeq) return;
        this.imageFail('Matching failed. Your image is still here, try again.', { retry: true, outcome: 'match_failed' });
        return;
      }
      if (seq !== this._searchSeq) return;
      this.finishImageResults(data, seq);
    }

    async finishImageResults(data, seq) {
      this.imageMode = {
        ...this.imageMode,
        phase: 'done',
        items: data.items || [],
        styles: data.styles || [],
        description: data.description || this.imageMode.description || '',
      };
      const items = this.imageMode.items;
      // The row is written server-side on /image/match; this id joins clicks
      this.imageMode.queryId = data.query_id || null;
      track('Discover Image Search', {
        query_id: this.imageMode.queryId,
        result_count: items.length,
        outcome: items.length > 0 ? 'matched' : 'no_matches',
      });
      this.renderImagePanels();
      this.chipsEl.innerHTML = '';
      this.dymEl.hidden = true;
      this.amiEl.hidden = true;
      this.renderMeta();
      this.moreEl.hidden = true;

      if (items.length === 0) {
        this.resultsEl.classList.remove('is-analyzing');
        this.gridEl.innerHTML = '';
        let chips = '';
        (this.imageMode.styles || []).forEach((style) => {
          chips += `<button type="button" class="dsc-chip" data-facet-key="styles" data-facet-value="${escapeHtml(style.name)}">${escapeHtml(style.name)}</button>`;
        });
        chips += '<button type="button" class="dsc-chip" data-dsc-image-clear>Back to text search</button>';
        this.zeroEl.innerHTML =
          '<h2 class="dsc-zero__title">No close visual matches</h2>' +
          '<p class="dsc-zero__hint">Try a tighter crop, like lettering, a texture, or one detail. Or explore the styles we spotted.</p>' +
          `<div class="dsc-zero__chips">${chips}</div>`;
        this.zeroEl.hidden = false;
        return;
      }
      const cards = await renderCards(items);
      if (seq !== this._searchSeq) return;
      this.resultsEl.classList.remove('is-analyzing');
      this.gridEl.innerHTML = '';
      cards.forEach((card) => this.gridEl.appendChild(card));
      syncWishlistHearts();
      this.zeroEl.hidden = true;
    }

    /** Analysis failed: leave the (parked) text results fully usable. */
    imageFail(message, { retry, outcome = 'unreadable' } = {}) {
      this.resultsEl.classList.remove('is-analyzing');
      track('Discover Image Search', { query_id: null, result_count: 0, outcome });
      this.imageMode = { ...this.imageMode, phase: 'error', error: message, canRetry: !!retry };
      this.renderImagePanels();
    }

    /** Pre-flight rejections (size) — never disturb the current results */
    imageNotice(message) {
      if (this.imageMode) {
        this.imageMode.notice = message;
        this.renderImagePanels();
      } else {
        this.toggleImageIntro(true, message);
      }
    }

    toggleImageIntro(open, notice) {
      this.imageIntro = !!open;
      if (!open) {
        // Only reclaim the strip if image mode isn't using it
        if (!this.imageMode) {
          this.imageStripEl.hidden = true;
          this.imageStripEl.innerHTML = '';
          this.imageStripEl.className = 'dsc-image-strip';
        }
        return;
      }
      this.imageStripEl.className = 'dsc-image-strip dsc-image-strip--intro';
      this.imageStripEl.innerHTML = this.imageIntroHtml(notice);
      this.imageStripEl.hidden = false;
    }

    /* One line, shown only for the homepage camera handoff (the picker can't
       open without a click) or when a file was refused. */
    imageIntroHtml(notice) {
      return (
        '<div class="dsc-image-intro">' +
        (notice
          ? `<span class="dsc-image-intro__text dsc-image-intro__text--error">${escapeHtml(notice)}</span>`
          : '<span class="dsc-image-intro__text">Drop an image here, or paste one</span>') +
        '<button type="button" class="button button--primary dsc-image-intro__btn" data-dsc-image-pick>Choose an image</button>' +
        `<button type="button" class="dsc-image-strip__clear" data-dsc-image-intro-close aria-label="Close">${ICONS.close}</button>` +
        '</div>'
      );
    }

    /** Both image-mode surfaces: rail panel (desktop) + strip (mobile) */
    renderImagePanels() {
      if (!this.imageMode) return;
      this.imageStripEl.className = 'dsc-image-strip dsc-image-strip--mode';
      this.imageStripEl.innerHTML = this.imageStripHtml();
      this.imageStripEl.hidden = false;
      this.facetsEl.classList.remove('is-stale');
      this.facetsEl.innerHTML = this.imagePanelHtml();
    }

    imageStepsHtml() {
      const phase = this.imageMode.phase;
      const mark = (state) =>
        state === 'doing' ? '<span class="dsc-spinner"></span>' : state === 'done' ? ICONS.check : '';
      const step = (label, state) =>
        `<li class="dsc-image-step is-${state}"><span class="dsc-image-step__mark">${mark(state)}</span>${label}</li>`;
      return (
        '<ol class="dsc-image-steps">' +
        step('Reading the image', phase === 'reading' ? 'doing' : 'done') +
        step('Finding matches', phase === 'reading' ? 'todo' : phase === 'matching' ? 'doing' : 'done') +
        '</ol>'
      );
    }

    imagePanelHtml() {
      const mode = this.imageMode;
      const busy = mode.phase === 'reading' || mode.phase === 'matching';
      let html = '<div class="dsc-image-panel">';
      html += `<div class="dsc-image-panel__head"><span class="dsc-kicker">Image search</span><button type="button" class="dsc-image-strip__clear" data-dsc-image-clear aria-label="Clear image search">${ICONS.close}</button></div>`;
      if (mode.thumbUrl) {
        html += `<img class="dsc-image-panel__thumb" src="${mode.thumbUrl}" alt="Your reference image">`;
      }
      if (mode.notice) html += `<p class="dsc-image-panel__error">${escapeHtml(mode.notice)}</p>`;

      if (mode.phase === 'error') {
        html += `<p class="dsc-image-panel__error">${escapeHtml(mode.error || '')}</p>`;
        html += '<div class="dsc-image-panel__actions">';
        if (mode.canRetry) {
          html += '<button type="button" class="button button--secondary dsc-image-panel__btn" data-dsc-image-rematch>Try again</button>';
        }
        html += '<button type="button" class="button button--secondary dsc-image-panel__btn" data-dsc-image-new>Another image</button>';
        html += '</div>';
      } else if (busy) {
        html += this.imageStepsHtml();
        if (mode.description) {
          html += `<p class="dsc-image-panel__desc">${escapeHtml(mode.description)}</p>`;
        }
      } else {
        if (mode.description) {
          html += '<span class="dsc-image-panel__label">What we see</span>';
          html += `<p class="dsc-image-panel__desc">${escapeHtml(mode.description)}</p>`;
        }
        const styles = mode.styles || [];
        if (styles.length > 0) {
          html += '<span class="dsc-image-panel__label">Detected styles</span><div class="dsc-image-panel__chips">';
          styles.forEach((style) => {
            html += `<button type="button" class="dsc-chip" data-facet-key="styles" data-facet-value="${escapeHtml(style.name)}" title="Leave image search and browse this style">${escapeHtml(style.name)}</button>`;
          });
          html += '</div>';
        }
        html += '<div class="dsc-image-panel__actions"><button type="button" class="button button--secondary dsc-image-panel__btn" data-dsc-image-new>Try another image</button></div>';
      }

      html += '<p class="dsc-image-panel__note">Not stored.</p></div>';
      return html;
    }

    imageStripHtml() {
      const mode = this.imageMode;
      const busy = mode.phase === 'reading' || mode.phase === 'matching';
      const status =
        mode.phase === 'reading'
          ? 'Reading the image…'
          : mode.phase === 'matching'
            ? 'Finding matches…'
            : mode.phase === 'error'
              ? mode.error
              : mode.description;
      let chips = '';
      if (mode.phase === 'done') {
        (mode.styles || []).forEach((style) => {
          chips += `<span class="dsc-chip">${escapeHtml(style.name)}</span>`;
        });
      } else if (mode.phase === 'error') {
        if (mode.canRetry) {
          chips += '<button type="button" class="dsc-chip" data-dsc-image-rematch>Try again</button>';
        }
        chips += '<button type="button" class="dsc-chip" data-dsc-image-new>Another image</button>';
      }
      return (
        (mode.thumbUrl
          ? `<img class="dsc-image-strip__thumb" src="${mode.thumbUrl}" alt="Your reference image">`
          : '') +
        '<span class="dsc-image-strip__body">' +
        `<span class="dsc-image-strip__desc">${busy ? '<span class="dsc-spinner dsc-image-strip__spinner"></span>' : ''}${escapeHtml(status || '')}</span>` +
        (mode.notice ? `<span class="dsc-image-strip__desc">${escapeHtml(mode.notice)}</span>` : '') +
        (chips ? `<span class="dsc-image-strip__chips">${chips}</span>` : '') +
        '</span>' +
        `<button type="button" class="dsc-image-strip__clear" data-dsc-image-clear aria-label="Clear image search">${ICONS.close}</button>`
      );
    }

    clearImageMode() {
      if (!this.imageMode) return;
      if (this.imageMode.thumbUrl) URL.revokeObjectURL(this.imageMode.thumbUrl);
      this.imageMode = null;
      this.imageStripEl.hidden = true;
      this.imageStripEl.innerHTML = '';
      this.imageStripEl.className = 'dsc-image-strip';
      this.resultsEl.classList.remove('is-analyzing');
      this.renderFacets(); // give the rail back to filters immediately
    }
  }

  window.customElements.define('discover-search-page', DiscoverSearchPage);


  /* ============================================================
     <discover-hero> — homepage hero
     The brand line, set in a font from the catalog. Search lives in
     the header capsule (Sep 2026): the hero's own input and its pill
     row were removed once the header carried both at every width.
     ============================================================ */

  /* What the heading needs before it can draw: the style's hash and the
     font's vertical metrics. Both survive a page load here, so a return
     visit draws from the image cache alone instead of two round trips
     before first paint. A replaced font file changes the hash, the render
     404s and the catch in showFont puts the text fallback back. */
  const HERO_CACHE_TTL = 24 * 60 * 60 * 1000;

  function heroCacheGet(key) {
    try {
      const entry = JSON.parse(localStorage.getItem(key) || 'null');
      return entry && Date.now() - entry.t < HERO_CACHE_TTL ? entry.v : null;
    } catch {
      return null;
    }
  }

  function heroCacheSet(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify({ t: Date.now(), v: value }));
    } catch {
      /* private mode */
    }
  }

  // The headline PNGs are rendered at this em size at scale 2, then shown
  // at the title's computed font-size (62px at most), so they stay at
  // retina density at every viewport from one cached render per line.
  const HERO_RENDER_EM = 62;
  // Credit line: gap below the heading's last baseline (Anthony: 10–20px under the ink, whatever font)
  const HERO_SETIN_GAP = 16;

  class DiscoverHero extends HTMLElement {
    connectedCallback() {
      // Nothing to re-wire on a DOM move (theme editor reorder): the
      // heading lays itself out again from the resize observer.
      if (this._wired) return;
      this._wired = true;

      this.fonts = this.readJsonAttr('data-fonts')
        .filter((font) => font && font.id)
        .map((font) => ({
          ...font,
          size: Number(font.size) > 0 ? Number(font.size) : 100,
          // the block stores line spacing as a percentage (105 = 1.05)
          leading: Number(font.leading) > 0 ? Number(font.leading) / 100 : 1.05,
          uppercase: font.uppercase === true,
          style: typeof font.style === 'string' ? font.style.trim() : '',
          wideLayout: typeof font.wideLayout === 'string' ? font.wideLayout : '',
          narrowLayout: typeof font.narrowLayout === 'string' ? font.narrowLayout : '',
        }));
      this.narrowQuery = window.matchMedia('(max-width: 740px)');
      // titleEl, not title: HTMLElement.title is the tooltip attribute
      this.titleEl = this.querySelector('.dsc-hero__title');
      this.reserveHeadingBox();
      this.wireHeading();
      if (window.Shopify && window.Shopify.designMode) this.wireEditorPreview();
    }

    disconnectedCallback() {
      if (this._headingObserver) this._headingObserver.disconnect();
      if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
    }

    readJsonAttr(name) {
      try {
        const parsed = JSON.parse(this.getAttribute(name) || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    /* The headline set in a catalog font: one random font block per visit.
       Each line is rendered as a transparent PNG at HERO_RENDER_EM (fit=ink:
       a frame that is a constant of the font with the baseline at a known
       offset) and the font's metrics are fetched alongside, so the images
       are laid out like text in that font at the block's own size and line
       spacing: cap height centred in the line box, both lines scaled
       together when the widest would overflow. Any failure leaves the
       theme-font text. */
    /* The line group a font uses at the current width: the block's own
       pick for wide or narrow screens, else the section default. A pick
       naming an empty layout falls back to the default. */
    groupFor(font) {
      const narrow = this.narrowQuery.matches;
      const fallback = this.titleEl
        ? this.titleEl.getAttribute(narrow ? 'data-hero-narrow' : 'data-hero-wide')
        : '';
      const wanted = font ? (narrow ? font.narrowLayout : font.wideLayout) : '';
      const byId = (id) => (id ? this.querySelector(`[data-hero-lines="${id}"]`) : null);
      return byId(wanted) || byId(fallback) || this.querySelector('[data-hero-lines]');
    }

    /* The lines the heading currently shows: the set font's group, or the
       section default before any font is set. */
    visibleLines() {
      const group = this.groupFor(this._heading ? this._heading.font : null);
      return group ? Array.from(group.querySelectorAll('[data-hero-line]')) : [];
    }

    /* Mark the group discover.js is about to fill so CSS shows it instead
       of the section default. */
    activateGroup(group) {
      this.querySelectorAll('[data-hero-lines]').forEach((candidate) =>
        candidate.classList.toggle('is-active', candidate === group)
      );
      if (this.titleEl) this.titleEl.classList.add('is-set');
    }

    wireHeading() {
      this.lines = this.visibleLines();
      if (this.lines.length === 0 || this.fonts.length === 0) {
        this.reveal();
        return;
      }
      // The editor previews whichever block is clicked, so it still resolves
      // every font up front; a visit only ever draws one.
      if (window.Shopify && window.Shopify.designMode) {
        FontPreviews.allStylesFor(this.fonts.map((font) => String(font.id))).then((styles) => {
          this._styles = styles;
          const usable = this.fonts.filter((font) => (styles[String(font.id)] || []).length > 0);
          if (usable.length === 0) return this.reveal();
          this.showFont(this.pickFont(usable));
        });
        return;
      }
      this.drawFirstUsable(this.visitOrder());
    }

    /* The visit's font first, the rest behind it as fallbacks. The pick is
       remembered for the session, so every later homepage view draws the
       same heading — and draws it from the browser's image cache. */
    visitOrder() {
      const pool = this.fonts.slice();
      let remembered = null;
      try {
        remembered = sessionStorage.getItem('dsc_hero_font');
      } catch {
        /* private mode */
      }
      const known = pool.findIndex((font) => font.blockId === remembered);
      const index = known >= 0 ? known : Math.floor(Math.random() * pool.length);
      return [...pool.splice(index, 1), ...pool];
    }

    /* Walks the order until a font answers with a renderable style: one
       small GET per font, and all but the first are only ever paid for when
       a block's product has no font file behind it. */
    drawFirstUsable(order) {
      const font = order[0];
      if (!font) {
        this.reveal();
        return;
      }
      this.loadStyles(font).then((styles) => {
        if (styles.length === 0) {
          this.drawFirstUsable(order.slice(1));
          return;
        }
        this._styles = Object.assign(this._styles || {}, { [String(font.id)]: styles });
        try {
          sessionStorage.setItem('dsc_hero_font', font.blockId);
        } catch {
          /* private mode */
        }
        this.showFont(font);
      });
    }

    loadStyles(font) {
      const key = `dsc:hero:styles:${font.id}`;
      const cached = heroCacheGet(key);
      if (cached) return Promise.resolve(cached);
      return FontPreviews.stylesForOne(String(font.id)).then((styles) => {
        if (styles.length > 0) heroCacheSet(key, styles);
        return styles;
      });
    }

    loadMetrics(hash) {
      const key = `dsc:hero:metrics:${hash}`;
      const cached = heroCacheGet(key);
      if (cached) return Promise.resolve(cached);
      return FontPreviews.metricsFor(hash).then((metrics) => {
        heroCacheSet(key, metrics);
        return metrics;
      });
    }

    /* The theme-font fallback is hidden from first paint (the section's
       inline script) so the hero never shows the wrong face and swaps. This
       puts the heading back on screen, drawn or not. */
    reveal() {
      this.classList.remove('is-awaiting');
    }

    /* Random outside the editor. Inside it, the block last clicked in the
       sidebar wins, and survives the section re-render a setting change
       triggers, so a font can be tuned without the pick changing under you. */
    pickFont(usable) {
      if (window.Shopify && window.Shopify.designMode) {
        let wanted = null;
        try {
          wanted = sessionStorage.getItem('dsc_hero_font');
        } catch {
          /* private mode */
        }
        const match = usable.find((font) => font.blockId === wanted);
        if (match) return match;
      }
      return usable[Math.floor(Math.random() * usable.length)];
    }

    wireEditorPreview() {
      this._onBlockSelect = (event) => {
        const blockId = event.detail && event.detail.blockId;
        const font = this.fonts.find((candidate) => candidate.blockId === blockId);
        if (!font) return;
        try {
          sessionStorage.setItem('dsc_hero_font', blockId);
        } catch {
          /* private mode */
        }
        if (this._styles) this.showFont(font);
      };
      document.addEventListener('shopify:block:select', this._onBlockSelect);
    }

    /* The block's style setting matches part of a style name; otherwise the
       first style, which is what the collection previews use too. */
    styleFor(font) {
      const styles = (this._styles && this._styles[String(font.id)]) || [];
      if (styles.length === 0) return null;
      if (font.style) {
        const wanted = font.style.toLowerCase();
        const match = styles.find((style) => String(style.name || '').toLowerCase().includes(wanted));
        if (match) return match;
      }
      return styles[0];
    }

    showFont(font) {
      const style = this.styleFor(font);
      const group = this.groupFor(font);
      if (!style || !group) {
        this.reveal();
        return;
      }
      const lines = Array.from(group.querySelectorAll('[data-hero-line]'));
      if (lines.length === 0) {
        this.reveal();
        return;
      }
      const token = (this._headingToken = (this._headingToken || 0) + 1);
      const setinEl = this.querySelector('[data-hero-setin]');
      const images = lines.map((line) => {
        const text = (line.getAttribute('data-hero-line') || line.textContent).trim();
        const img = new Image();
        img.alt = font.uppercase ? text.toUpperCase() : text;
        img.decoding = 'async';
        img.src = FontPreviews.src(style.hash, img.alt, null, 'transparent', 'FFFFFF', {
          size: HERO_RENDER_EM,
          scale: 2,
          fit: 'ink',
        });
        return img;
      });
      const loads = images.map(
        (img) =>
          new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          })
      );
      Promise.all([this.loadMetrics(style.hash), ...loads])
        .then(([metrics]) => {
          if (token !== this._headingToken) return; // a later pick won
          this.lines = lines;
          this._heading = { font, group, images, metrics };
          this.activateGroup(group);
          this.reserveHeadingBox();
          // The credit is filled before the layout runs: it is part of the
          // block being centred, so its height has to be measurable. It
          // stays hidden until the layout has put it under the baseline.
          if (setinEl) {
            setinEl.innerHTML = `Featured: <a href="${escapeHtml(font.url)}">${escapeHtml(font.title)}</a>`;
          }
          this.layoutHeading();
          lines.forEach((line, index) => line.replaceChildren(images[index]));
          if (setinEl) setinEl.classList.remove('is-pending');
          this.reveal();
        })
        .catch(() => {
          this.reveal(); // the theme font is the fallback
        });
    }

    /* The heading box is sized for the largest configured font from first
       paint (size x line spacing per line), and the lines sit centred in it,
       so the hero has one height whatever font a visit draws and the swap
       from text to image moves nothing. Re-run on resize: the base size is
       the fluid clamp. */
    reserveHeadingBox() {
      if (!this.titleEl || this.fonts.length === 0) return;
      const style = getComputedStyle(this.titleEl);
      const base = parseFloat(style.fontSize) || 0;
      const textLeading = parseFloat(style.getPropertyValue('--dsc-hero-leading')) || 1.05;
      const lineCount = (group) => (group ? group.querySelectorAll('[data-hero-line]').length : 0);
      // each font's own layout at this width, at its own size and spacing
      const tallest = Math.max(
        lineCount(this.groupFor(null)) * textLeading,
        ...this.fonts.map((font) => lineCount(this.groupFor(font)) * (font.size / 100) * font.leading)
      );
      this.titleEl.style.minHeight = `${Math.ceil(tallest * base)}px`;
      if (!this._headingObserver) {
        this._headingObserver = new ResizeObserver(() => {
          this.reserveHeadingBox();
          this.syncLineGroup();
          this.layoutHeading();
        });
        this._headingObserver.observe(this);
      }
    }

    /* Crossing the phone breakpoint can change a font's layout, so the
       shown font is rendered again for the new lines. */
    syncLineGroup() {
      if (!this._heading) return;
      const { font, group } = this._heading;
      if (this.groupFor(font) === group) return;
      this._heading = null;
      this.showFont(font);
    }

    /* Lay the rendered lines out as text in the picked font would be: the
       em is the title's computed font-size (the clamp) times the block's
       size, the line box is the block's line spacing x em, the cap height
       sits centred in the box. If the widest line would overflow the title,
       both lines scale down by the same factor so they keep one size.
       Then the whole thing is centred by its INK — cap top of the first
       line to the last baseline, plus the credit under it — inside the box
       reserved for the tallest font in the section. Centring the boxes
       instead would leave every font but the tallest sitting high, by its
       own leading and by whatever the reserved box left over. */
    layoutHeading() {
      const heading = this._heading;
      if (!heading || !this.titleEl) return;
      const { font, images, metrics } = heading;
      const available = this.titleEl.clientWidth;
      const base = parseFloat(getComputedStyle(this.titleEl).fontSize) * (font.size / 100);
      // natural sizes are at scale 2 of HERO_RENDER_EM: convert to em units
      const emWidths = images.map((img) => img.naturalWidth / 2 / HERO_RENDER_EM);
      const widest = Math.max(...emWidths);
      const fitScale = widest > 0 && available > 0 ? Math.min(1, available / (widest * base)) : 1;
      const em = base * fitScale;
      const lineBox = font.leading * em;
      const baseline = (lineBox + metrics.capHeight * em) / 2;
      this.lines.forEach((line, index) => {
        const img = images[index];
        line.style.height = `${lineBox}px`;
        img.style.width = `${emWidths[index] * em}px`;
        img.style.height = `${(img.naturalHeight / 2 / HERO_RENDER_EM) * em}px`;
        img.style.top = `${baseline - metrics.frameTop * em}px`;
      });

      // The reserved box, not the laid-out height: measuring what we are
      // about to shift would feed the shift back into itself
      const titleStyle = getComputedStyle(this.titleEl);
      const reserved = Math.max(
        parseFloat(titleStyle.minHeight) || 0,
        this.lines.length * lineBox
      );
      const column = reserved + (parseFloat(titleStyle.marginBottom) || 0);
      const setinEl = this.querySelector('[data-hero-setin]');
      const credit = setinEl ? HERO_SETIN_GAP + setinEl.offsetHeight : 0;
      // cap top within the first line box, and the ink's own height
      const capTop = (lineBox - metrics.capHeight * em) / 2;
      const ink = (this.lines.length - 1) * lineBox + baseline - capTop;
      const shift = Math.round((column - (ink + credit)) / 2 - capTop);
      this.lines[0].style.marginTop = `${shift}px`;

      // The credit sits a fixed gap under the last line's baseline, so it
      // follows the font that drew rather than the reserved box
      if (setinEl) {
        const lastBaseline =
          this.titleEl.offsetTop + shift + (this.lines.length - 1) * lineBox + baseline;
        setinEl.style.top = `${Math.round(lastBaseline + HERO_SETIN_GAP)}px`;
      }
    }
  }

  window.customElements.define('discover-hero', DiscoverHero);

  // The brief canvas lives in discover-brief.js (split out 2026-09-19 so the
  // homepage stops render-blocking on a feature that renders nowhere). It is
  // a separate IIFE, so the four things it borrows from this scope are handed
  // over deliberately rather than duplicated. Keep this in step with
  // discover-brief.js if any of them change shape.
  window.DiscoverShared = { API_BASE, escapeHtml, sessionId, FontPreviews };
})();
